import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import { Link, useParams } from "react-router-dom";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import CareRichTextEditor from "../../components/care/CareRichTextEditor";
import InitialCareSnapshotForm from "../../components/care/InitialCareSnapshotForm";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import { useFileUpload } from "../../hooks/useFileUpload";
import careService from "../../services/careService";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";

const bioFields = [
  ["firstName", "First name", "text"],
  ["middleName", "Middle name", "text"],
  ["lastName", "Last name", "text"],
  ["preferredName", "Preferred name", "text"],
  ["dateOfBirth", "Date of birth", "date"],
  ["socialSecurityNumber", "Social Security Number", "text"],
  ["email", "Email", "email"],
];

const sexOptions = [
  "Female",
  "Male",
  "Intersex",
  "Non-binary",
  "Transgender female",
  "Transgender male",
  "Two-Spirit",
  "Questioning",
  "Not listed",
  "Prefer not to say",
  "Unknown",
  "Other",
];
const raceOptions = [
  "American Indian or Alaska Native",
  "Asian",
  "Black or African American",
  "Hispanic or Latino",
  "Middle Eastern or North African",
  "Native Hawaiian or Other Pacific Islander",
  "White",
  "Multiracial",
  "Other",
  "Prefer not to say",
  "Unknown",
];
const maritalStatusOptions = ["Single", "Married", "Domestic partnership", "Separated", "Divorced", "Widowed", "Other", "Prefer not to say", "Unknown"];
const codeStatusOptions = ["CPR", "DNR", "Full Code", "Comfort Care", "Unknown"];
const yesNoOptions = ["Yes", "No"];
const APPLICATION_REFRESH_DELAY_MS = 650;

const refreshApplicationSoon = () => {
  if (typeof window === "undefined") return;
  window.setTimeout(() => window.location.reload(), APPLICATION_REFRESH_DELAY_MS);
};

const addressFields = [
  ["address", "Address"],
  ["city", "City"],
  ["county", "County"],
  ["state", "State"],
  ["country", "Country"],
  ["zip", "Zip Code"],
];

const addressTypeOptions = ["Home", "Office", "Business", "Permanent", "Other"];
const usStateOptions = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "District of Columbia",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];
const usStateAbbreviations = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};
const phoneFields = [
  ["mobilePhone", "Mobile phone"],
  ["officePhone", "Office phone"],
  ["homePhone", "Home phone"],
  ["fax", "Fax"],
];
const preferredPhoneOptions = [
  ["mobilePhone", "Mobile"],
  ["officePhone", "Office"],
  ["homePhone", "Home"],
];

const assessmentItems = [
  "Medication list and medication behaviors",
  "Diagnosis and medical history",
  "Care needs and supervision level",
  "Behavior, welfare, and safety notes",
  "Emergency contacts and PoA details",
];

const contactRelationshipOptions = [
  "Spouse",
  "Daughter",
  "Son",
  "Parent",
  "Sibling",
  "Grandchild",
  "Emergency Contact",
  "Power of Attorney",
  "Legal Guardian",
  "Friend",
  "Case Manager",
  "Social Worker / Case Manager",
  "Financial Worker",
  "Nurse Delegator",
  "Pharmacy",
  "Primary Care Doctor",
  "Specialist",
  "Other",
];

const insuranceFields = [
  ["medicareNumber", "Medicare Number"],
  ["medicaidNumber", "Medicaid Number"],
];

const admissionDetailsFields = [
  ["hospitalName", "Hospital name", "text"],
  ["hospitalContactName", "Contact person", "text"],
  ["hospitalPhone", "Phone", "tel"],
  ["hospitalEmail", "Email", "email"],
  ["admissionDate", "Admission date", "date"],
  ["dischargeDate", "Discharge date", "date"],
];

const priorFamilyHomeFields = [
  ["priorFamilyHomeName", "Family home name"],
  ["priorFamilyHomeContactName", "Contact person"],
  ["priorFamilyHomePhone", "Phone"],
  ["priorFamilyHomeEmail", "Email"],
  ["priorFamilyHomeYears", "Years"],
  ["priorFamilyHomeMonths", "Months"],
];

const priorCaregiverFields = [
  ["priorCaregiverName", "Name"],
  ["priorCaregiverRelationship", "Relationship"],
  ["priorCaregiverPhone", "Phone"],
  ["priorCaregiverEmail", "Email"],
];

const otherInsuranceFields = [
  ["provider", "Insurance provider"],
  ["policyNumber", "Member / policy number"],
  ["groupNumber", "Group number"],
  ["notes", "Notes"],
];

const signedDocumentDefaults = [
  "Admission agreement",
  "Resident rights and responsibilities",
  "House policies",
  "Privacy / HIPAA acknowledgement",
  "Medication administration policy",
  "Emergency evacuation plan",
  "Smoking policy",
  "Financial agreement",
];

const intakeSteps = [
  ["source", "Source", "Choose manual entry or state assessment import"],
  ["bio", "Bio Data", "Resident identity, address, and demographics"],
  ["contacts", "Contacts", "Family, PoA, guardian, and emergency contacts"],
  ["careHistory", "Care History", "Admission source and previous care details"],
  ["care", "Care Snapshot", "Diagnosis, medications, care needs, and safety"],
  ["preliminaryPlan", "Preliminary Plan", "Provider initial care plan after care snapshot"],
  ["documents", "Documents", "Upload files and validate signed policies"],
  ["review", "Review", "Confirm and submit resident intake"],
];

const NEW_RESIDENT_DRAFT_KEY = "olreadia-care:new-resident-draft";

const onboardingStepStatusFor = (stepIndex = 0, savedAt = new Date().toISOString(), statusOverride = "") => {
  const safeIndex = Math.max(0, Math.min(stepIndex, intakeSteps.length - 1));
  const current = intakeSteps[safeIndex];
  const status =
    statusOverride ||
    (safeIndex === 0 ? "NOT_STARTED" : safeIndex === intakeSteps.length - 1 ? "READY_TO_SUBMIT" : "IN_PROGRESS");
  return {
    status,
    currentStepIndex: safeIndex,
    currentStepKey: current[0],
    currentStepLabel: current[1],
    lastSavedStepIndex: safeIndex,
    lastSavedStepKey: current[0],
    lastSavedStepLabel: current[1],
    completedSteps: intakeSteps.slice(0, safeIndex).map(([key, label], index) => ({ index, key, label })),
    remainingSteps: intakeSteps.slice(safeIndex + 1).map(([key, label], offset) => ({
      index: safeIndex + offset + 1,
      key,
      label,
    })),
    canResume: safeIndex > 0 && status !== "SUBMITTED",
    lastSavedAt: savedAt,
  };
};

const contactField = (key, field) => `${key}${field[0].toUpperCase()}${field.slice(1)}`;

const addressFieldName = (prefix, field) => (prefix ? contactField(prefix, field) : field);

const preferredPhoneLabel = (value) => preferredPhoneOptions.find(([field]) => field === value)?.[1] || "";

const apiSexValue = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "male") return "MALE";
  if (normalized === "female") return "FEMALE";
  return value ? "OTHER" : null;
};

const displaySexValue = (value = "") => {
  const raw = normalizeExtractionText(String(value || "").replace(/[|,;]+$/g, ""));
  if (!raw) return "";
  const normalized = raw.replace(/_/g, " ").toLowerCase();
  const embeddedSex = sexOptions.find((option) => new RegExp(`\\b${option.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(raw));
  if (embeddedSex) return embeddedSex;
  return sexOptions.find((option) => option.toLowerCase() === normalized) ||
    normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const mergeFilledObjects = (...objects) =>
  objects.reduce((merged, object) => {
    if (!object || typeof object !== "object" || Array.isArray(object)) return merged;
    Object.entries(object).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        merged[key] = value;
      }
    });
    return merged;
  }, {});

const yesNoBoolean = (value) => {
  if (value === "Yes") return true;
  if (value === "No") return false;
  return null;
};

const splitName = (name = "") => {
  const cleaned = normalizeExtractionText(String(name || "")
    .replace(/[|,;]+$/g, "")
    .replace(/\b(Female|Male|Intersex|Non-binary|Transgender female|Transgender male|Two-Spirit|Questioning|Unknown|Other)\b.*$/i, ""));
  if (!cleaned || /^\d+$/.test(cleaned)) return { firstName: "", middleName: "", lastName: "" };
  if (cleaned.includes(",")) {
    const [last, rest] = cleaned.split(",").map((part) => part.trim()).filter(Boolean);
    const restParts = String(rest || "").split(/\s+/).filter(Boolean);
    if (last && restParts.length) {
      return {
        firstName: restParts[0],
        middleName: restParts.slice(1).join(" "),
        lastName: last,
      };
    }
  }
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: "", middleName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], middleName: "", lastName: "" };
  return {
    firstName: parts[0],
    middleName: parts.length > 2 ? parts.slice(1, -1).join(" ") : "",
    lastName: parts[parts.length - 1],
  };
};

const addressPayload = (formData, prefix = "", { includeAddressType = true } = {}) => {
  const fieldName = (field) => addressFieldName(prefix, field);
  const addressType = formData[fieldName("addressType")] || "";
  return {
    address: formData[fieldName("address")] || "",
    addressLine2: formData[fieldName("addressLine2")] || "",
    city: formData[fieldName("city")] || "",
    county: formData[fieldName("county")] || "",
    state: formData[fieldName("state")] || "",
    country: formData[fieldName("country")] || "",
    zipCode: formData[fieldName("zip")] || "",
    latitude: formData[fieldName("latitude")] || null,
    longitude: formData[fieldName("longitude")] || null,
    placeId: formData[fieldName("placeId")] || "",
    addressType: includeAddressType ? addressType : "",
    otherAddressType: includeAddressType && addressType === "Other" ? formData[fieldName("addressTypeOther")] || "" : "",
  };
};

const phonePayload = (formData, prefix = "") => {
  const fieldName = (field) => (prefix ? contactField(prefix, field) : field);
  return {
    mobile: formData[fieldName("mobilePhone")] || "",
    office: formData[fieldName("officePhone")] || "",
    home: formData[fieldName("homePhone")] || "",
    fax: formData[fieldName("fax")] || "",
    preferred: phoneValueFor(formData, prefix),
  };
};

const personPayload = ({ formData, name, prefix = "", relationship = "", email = "", notes = "" }) => {
  const parts = splitName(name);
  return {
    ...parts,
    relationship,
    email,
    phones: phonePayload(formData, prefix),
    address: addressPayload(formData, prefix),
    notes,
  };
};

const contactTypeFor = (relationship = "") => {
  const normalized = relationship.toLowerCase();
  if (normalized.includes("social worker") || normalized.includes("case manager")) return "SOCIAL_WORKER";
  if (normalized.includes("power of attorney")) return "POWER_OF_ATTORNEY";
  if (normalized.includes("guardian")) return "GUARDIAN";
  if (normalized.includes("emergency")) return "EMERGENCY_CONTACT";
  if (normalized.includes("pharmacy")) return "PHARMACY";
  if (normalized.includes("doctor") || normalized.includes("physician")) return "PRIMARY_CARE_DOCTOR";
  if (normalized.includes("specialist")) return "SPECIALIST";
  return "OTHER";
};

const phoneValueFor = (formData, prefix = "") => {
  const fieldName = (field) => (prefix ? contactField(prefix, field) : field);
  const preferredPhone = formData[fieldName("preferredPhone")];
  return (
    (preferredPhone ? formData[fieldName(preferredPhone)] : "") ||
    formData[fieldName("mobilePhone")] ||
    formData[fieldName("officePhone")] ||
    formData[fieldName("homePhone")] ||
    ""
  );
};

const preferredPhoneFieldFor = (preferredValue, phoneValues = {}) => {
  const normalized = String(preferredValue || "").trim();
  if (!normalized) return "";
  if (preferredPhoneOptions.some(([field]) => field === normalized)) return normalized;
  return preferredPhoneOptions.find(([field]) => String(phoneValues[field] || "").trim() === normalized)?.[0] || "";
};

const yesNoFromBoolean = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "";
};

const applyAddressToForm = (target, address = {}, prefix = "") => {
  const fieldName = (field) => addressFieldName(prefix, field);
  const assignIfPresent = (field, value) => {
    if (value !== null && value !== undefined && value !== "") {
      target[fieldName(field)] = value;
    }
  };
  assignIfPresent("addressSearch", address.formattedAddress || address.address || address.addressLine1);
  assignIfPresent("address", address.address || address.addressLine1 || address.formattedAddress);
  assignIfPresent("addressLine2", address.addressLine2);
  assignIfPresent("city", address.city);
  assignIfPresent("county", address.county);
  assignIfPresent("state", address.state);
  assignIfPresent("country", address.country);
  assignIfPresent("zip", address.zipCode || address.zip);
  assignIfPresent("latitude", address.latitude);
  assignIfPresent("longitude", address.longitude);
  assignIfPresent("placeId", address.placeId);
  assignIfPresent("addressType", address.addressType);
  assignIfPresent("addressTypeOther", address.otherAddressType || address.addressTypeOther);
};

const cleanContactNotes = (value = "", name = "", relationship = "", phoneValues = {}, email = "") =>
  String(value || "")
    .split("|")
    .map((part, index) => {
      const normalizedPart = part.toLowerCase();
      const phone = Object.values(phoneValues).find(Boolean) || "";
      if (!part) return "";
      if (name && normalizedPart.includes(String(name).toLowerCase())) return "";
      if (phone && part.replace(/\D/g, "").includes(String(phone).replace(/\D/g, ""))) return "";
      if (email && normalizedPart.includes(String(email).toLowerCase())) return "";
      if (index === 0) {
        const relationshipMatch = part.match(/\b(daughter|son|spouse|partner|sibling|neighbor|friend|caregiver|pharmacy)\b/i);
        if (relationshipMatch) {
          return part.slice(relationshipMatch.index + relationshipMatch[0].length).replace(/^[\s,;:|-]+/, "").trim();
        }
        if (/\b[A-Z][a-z]+(?:\s+[A-Z][a-z.'-]+)+\b/.test(part) && (phone || email)) return "";
      }
      if (relationship && normalizedPart === String(relationship).toLowerCase()) return "";
      return part.trim();
    })
    .filter(Boolean)
    .join(" | ");

const apiContactToForm = (contact = {}, index = 0) => {
  const prefix = `residentContactLoaded${index + 1}`;
  const address = {
    ...(contact.address || contact.locationInformation || contact.location || {}),
    addressType: contact.addressType || contact.address?.addressType || contact.locationInformation?.addressType || contact.location?.addressType || "",
    otherAddressType:
      contact.otherAddressType ||
      contact.addressTypeOther ||
      contact.address?.otherAddressType ||
      contact.address?.addressTypeOther ||
      contact.locationInformation?.otherAddressType ||
      contact.locationInformation?.addressTypeOther ||
      contact.location?.otherAddressType ||
      contact.location?.addressTypeOther ||
      "",
  };
  const relationship = contact.relationship || contact.contactType || contact.type || "";
  const knownRelationship = contactRelationshipOptions.includes(relationship);
  const phoneValues = {
    mobilePhone: contact.mobilePhone || contact.phone || contact.phoneNumber || contact.primaryPhone || contact.phones?.mobile || "",
    officePhone: contact.officePhone || contact.workPhone || contact.businessPhone || contact.phones?.office || contact.phones?.work || "",
    homePhone: contact.homePhone || contact.secondaryPhone || contact.phones?.home || "",
    fax: contact.fax || contact.faxNumber || contact.phones?.fax || "",
  };
  const formData = {
    [contactField(prefix, "name")]: contact.name || contact.fullName || [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(" ") || "",
    [contactField(prefix, "organizationName")]: contact.organizationName || "",
    [contactField(prefix, "email")]: contact.email || "",
    [contactField(prefix, "mobilePhone")]: phoneValues.mobilePhone,
    [contactField(prefix, "officePhone")]: phoneValues.officePhone,
      [contactField(prefix, "homePhone")]: phoneValues.homePhone,
      [contactField(prefix, "fax")]: phoneValues.fax,
      [contactField(prefix, "preferredPhone")]: preferredPhoneFieldFor(contact.preferredPhone || contact.phones?.preferred, phoneValues),
  };
  applyAddressToForm(formData, address, prefix);
  return {
    contact: {
      id: contact.id || `loaded-contact-${index + 1}`,
      prefix,
      type: knownRelationship ? relationship : relationship ? "Other" : "",
      relationshipOther: knownRelationship ? "" : relationship,
      notes: cleanContactNotes(
        contact.notes,
        formData[contactField(prefix, "name")],
        relationship,
        phoneValues,
        formData[contactField(prefix, "email")],
      ),
    },
    formData,
  };
};

const extractResidentContacts = (preview) => {
  const root = preview?.previewPayload || preview?.payload || preview || {};
  const stages = root.onboardingStages || root.previewPayload?.onboardingStages || root.payload?.onboardingStages || {};
  const contactsStage = stages.contacts || root.contacts || preview?.contacts || {};
  const candidates = [
    contactsStage.collateralContacts,
    contactsStage.colleteralContacts,
    contactsStage.contacts,
    contactsStage.payload?.collateralContacts,
    contactsStage.payload?.colleteralContacts,
    contactsStage.payload?.contacts,
    contactsStage.payload?.resident?.collateralContacts,
    contactsStage.payload?.resident?.colleteralContacts,
    contactsStage.payload?.resident?.contacts,
    contactsStage.payload,
    contactsStage,
    stages.bio?.collateralContacts,
    stages.bio?.colleteralContacts,
    stages.bio?.contacts,
    stages.bio?.resident?.collateralContacts,
    stages.bio?.resident?.colleteralContacts,
    stages.bio?.resident?.contacts,
    stages.bio?.sourceForm?.contacts,
    stages.bio?.sourceForm?.collateralContacts,
    stages.bio?.sourceForm?.colleteralContacts,
    stages.resident?.collateralContacts,
    stages.resident?.colleteralContacts,
    stages.resident?.contacts,
    root.resident?.collateralContacts,
    root.resident?.colleteralContacts,
    root.resident?.contacts,
    preview?.resident?.collateralContacts,
    preview?.resident?.colleteralContacts,
    preview?.resident?.contacts,
    preview?.resident?.contactInformation?.contacts,
    root.contactInformation?.contacts,
    preview?.contactInformation?.contacts,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length) return candidate;
    if (Array.isArray(candidate?.contacts) && candidate.contacts.length) return candidate.contacts;
  }
  return [];
};

const mergeContactSources = (...contactLists) => {
  const byContent = new Map();
  contactLists
    .flat()
    .filter((contact) => contact && typeof contact === "object")
    .forEach((contact) => {
      const name = String(contact.name || contact.fullName || contact.organizationName || [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(" ")).trim().toLowerCase();
      const relationship = String(contact.relationship || contact.contactType || contact.type || "").trim().toLowerCase();
      const email = String(contact.email || contact.primaryEmail || "").trim().toLowerCase();
      const phones = contact.phones || {};
      const phone = String(contact.mobilePhone || contact.phone || contact.phoneNumber || contact.primaryPhone || phones.mobile || phones.office || phones.home || phones.preferred || "").trim();
      const key = `${name}|${relationship}|${email}|${phone}`;
      if (!key.replace(/\|/g, "")) return;
      const existing = byContent.get(key);
      byContent.set(key, existing && isUuid(existing.id) ? existing : { ...contact, id: isUuid(contact.id) ? contact.id : existing?.id || contact.id });
    });
  return Array.from(byContent.values());
};

const withoutContactFormFields = (formData = {}) =>
  Object.fromEntries(Object.entries(formData).filter(([key]) => !key.startsWith("residentContact")));

const hydrateDraftFromPreview = (preview = {}) => {
  const root = preview.previewPayload || preview.payload || preview || {};
  const stages = root.onboardingStages || root.previewPayload?.onboardingStages || root.payload?.onboardingStages || {};
  const bio = stages.bio || {};
  const sourceForm = bio.sourceForm || {};
  const formData = { ...(sourceForm.formData || {}) };
  const resident = mergeFilledObjects(root.resident, preview.resident, stages.resident);
  const personal = mergeFilledObjects(resident.personalInformation, bio.personal, bio.personalInformation);
  const demographics = mergeFilledObjects(resident.demographics, bio.residentDemographics, bio.demographics);
  const coverage = bio.healthAndCoverage || {};
  formData.firstName = firstBioValue(personal.firstName, resident.firstName, formData.firstName);
  formData.middleName = firstBioValue(personal.middleName, resident.middleName, formData.middleName);
  formData.lastName = firstBioValue(personal.lastName, resident.lastName, formData.lastName);
  formData.preferredName = firstBioValue(personal.preferredName, resident.preferredName, formData.preferredName);
  formData.dateOfBirth = dateInputValueFrom(firstBioValue(personal.birthDate, personal.dateOfBirth, personal.dob, resident.birthDate, resident.dateOfBirth, resident.dob, formData.dateOfBirth));
  formData.sex = displaySexValue(firstBioValue(personal.sex, personal.gender, demographics.sex, demographics.gender, resident.sex, resident.gender, formData.sex));
  formData.socialSecurityNumber = firstBioValue(personal.socialSecurityNumber, resident.socialSecurityNumber, formData.socialSecurityNumber);
  formData.primaryLanguage = firstBioValue(personal.primaryLanguage, personal.language, personal.preferredLanguage, demographics.primaryLanguage, demographics.language, demographics.preferredLanguage, resident.primaryLanguage, resident.language, resident.preferredLanguage, formData.primaryLanguage);
  formData.heightFeet = firstBioValue(personal.heightFeet, resident.heightFeet, formData.heightFeet);
  formData.heightInches = firstBioValue(personal.heightInches, resident.heightInches, formData.heightInches);
  formData.weightLbs = firstBioValue(personal.weightLbs, resident.weightLbs, formData.weightLbs);
  formData.race = firstBioValue(personal.race, resident.race, formData.race);
  formData.raceOther = firstBioValue(personal.raceOther, resident.raceOther, formData.raceOther);
  formData.maritalStatus = firstBioValue(personal.maritalStatus, resident.maritalStatus, formData.maritalStatus);
  formData.placeOfBirth = firstBioValue(personal.placeOfBirth, resident.placeOfBirth, formData.placeOfBirth);
  formData.religion = firstBioValue(personal.religion, resident.religion, formData.religion);
  formData.codeStatus = firstBioValue(coverage.codeStatus, resident.codeStatus, formData.codeStatus);
  formData.advancedDirectives = firstBioValue(
    yesNoFromBoolean(coverage.advancedDirectives),
    yesNoFromBoolean(resident.advancedDirectives),
    formData.advancedDirectives,
  );
  formData.medicareNumber = firstBioValue(coverage.medicareNumber, resident.medicareNumber, formData.medicareNumber);
  formData.medicaidNumber = firstBioValue(coverage.medicaidNumber, resident.medicaidNumber, formData.medicaidNumber);
  const supplementaryInsurances = normalizeSupplementaryInsurances(
    [coverage.supplementaryInsurances, resident.supplementaryInsurances]
      .find((value) => Array.isArray(value) && value.length) || [],
  );
  if (supplementaryInsurances.length) {
    formData.hasOtherInsurance = "Yes";
  } else {
    formData.hasOtherInsurance = formData.hasOtherInsurance || "";
  }
  formData.bioNotes = firstBioValue(bio.notes, bio.backgroundNotes, resident.notes, resident.backgroundNotes, formData.bioNotes);
  applyAddressToForm(formData, bio.address);
  applyAddressToForm(formData, resident.locationInformation || resident.address || {});
  const residentContactInfo = resident.contactInformation || {};
  const residentPhones = residentContactInfo.phones || {};
  formData.mobilePhone = firstBioValue(residentContactInfo.mobilePhone, residentContactInfo.mobile, residentContactInfo.phone, residentContactInfo.phoneNumber, residentPhones.mobile, residentPhones.primary, residentContactInfo.primaryPhone, formData.mobilePhone);
  formData.officePhone = firstBioValue(residentContactInfo.officePhone, residentContactInfo.workPhone, residentContactInfo.businessPhone, residentPhones.office, residentPhones.work, formData.officePhone);
  formData.homePhone = firstBioValue(residentContactInfo.homePhone, residentPhones.home, residentContactInfo.secondaryPhone, formData.homePhone);
  formData.preferredPhone =
    preferredPhoneFieldFor(residentPhones.preferred || residentContactInfo.preferredPhone, formData) ||
    (residentContactInfo.primaryPhone ? "mobilePhone" : "");
  formData.email = firstBioValue(residentContactInfo.primaryEmail, residentContactInfo.email, resident.primaryContactEmail, resident.email, formData.email);
  formData.addressType = firstBioValue(resident.residentAddressType, formData.addressType);
  formData.addressTypeOther = firstBioValue(resident.residentOtherAddressType, formData.addressTypeOther);

  const contactsPayload = mergeContactSources(extractResidentContacts(preview), stages.contacts?.contacts || []);
  const loadedContacts = contactsPayload.map(apiContactToForm);
  loadedContacts.forEach((entry) => Object.assign(formData, entry.formData));

  const careHistoryStage = stages.careHistory || root.careHistory || {};
  const careHistory = careHistoryStage.careHistory || careHistoryStage.payload?.careHistory || {};
  formData.wasAdmittedAtHospital = yesNoFromBoolean(careHistory.admittedAtHospital);
  if (careHistory.hospitalAdmission) {
    formData.hospitalName = careHistory.hospitalAdmission.name || "";
    formData.hospitalContactName = careHistory.hospitalAdmission.contactName || "";
    formData.hospitalPhone = careHistory.hospitalAdmission.phones?.preferred || careHistory.hospitalAdmission.phones?.mobile || "";
    formData.hospitalEmail = careHistory.hospitalAdmission.email || "";
    formData.admissionDescription = careHistory.hospitalAdmission.notes || "";
    applyAddressToForm(formData, careHistory.hospitalAdmission.address, "hospital");
  }
  formData.fromAnotherFamilyHome = yesNoFromBoolean(careHistory.comingFromAnotherFamilyHome);
  if (careHistory.previousFamilyHome) {
    formData.priorFamilyHomeName = careHistory.previousFamilyHome.name || "";
    formData.priorFamilyHomeContactName = careHistory.previousFamilyHome.contactName || "";
    formData.priorFamilyHomePhone = careHistory.previousFamilyHome.phones?.preferred || careHistory.previousFamilyHome.phones?.mobile || "";
    formData.priorFamilyHomeEmail = careHistory.previousFamilyHome.email || "";
    formData.priorFamilyHomeYears = careHistory.previousFamilyHome.years || "";
    formData.priorFamilyHomeMonths = careHistory.previousFamilyHome.months || "";
    formData.priorFamilyHomeDetails = plainTextFromHtml(careHistory.previousFamilyHome.notes);
    applyAddressToForm(formData, careHistory.previousFamilyHome.address, "priorFamilyHome");
  }
  formData.hadPriorCaregiver = yesNoFromBoolean(careHistory.hadPriorCaregiver);
  if (careHistory.priorCaregiver) {
    formData.priorCaregiverName = [careHistory.priorCaregiver.firstName, careHistory.priorCaregiver.middleName, careHistory.priorCaregiver.lastName].filter(Boolean).join(" ");
    formData.priorCaregiverRelationship = careHistory.priorCaregiver.relationship || "";
    formData.priorCaregiverPhone = careHistory.priorCaregiver.phones?.preferred || careHistory.priorCaregiver.phones?.mobile || "";
    formData.priorCaregiverEmail = careHistory.priorCaregiver.email || "";
    formData.priorCaregiverDetails = plainTextFromHtml(careHistory.priorCaregiver.notes);
    applyAddressToForm(formData, careHistory.priorCaregiver.address, "priorCaregiver");
  }

  const snapshot = stages.careSnapshot?.initialCareSnapshot || {};
  formData.diagnosis = snapshot.diagnosis || "";
  formData.allergies = snapshot.allergies || "";
  formData.diet = snapshot.diet || "";
  formData.medicationNotes = snapshot.medicationNotes || "";
  formData.careNeeded = snapshot.careNeeded || "";
  formData.behaviorSafetyNotes = snapshot.behaviorSafetyNotes || "";
  formData.preliminaryPlanGoals = cleanUserFacingExtractionText(snapshot.preliminaryPlan?.goals || "");
  formData.preliminaryPlanCareApproach = cleanUserFacingExtractionText(snapshot.preliminaryPlan?.careApproach || "");
  formData.preliminaryPlanStaffInstructions = cleanUserFacingExtractionText(snapshot.preliminaryPlan?.staffInstructions || "");
  formData.preliminaryPlanFamilyPreferences = cleanUserFacingExtractionText(snapshot.preliminaryPlan?.familyPreferences || "");
  formData.preliminaryPlanRisksAndFollowUp = cleanUserFacingExtractionText(snapshot.preliminaryPlan?.risksAndFollowUp || "");

  const documents = stages.documents?.documents || {};
  const uploadedDocuments = documents.uploadedDocuments || [];
  return {
    assessmentExtraction: sourceForm.assessmentExtraction || null,
    formData,
    contacts: loadedContacts.map((entry) => entry.contact),
    otherInsurances: normalizeSupplementaryInsurances(sourceForm.otherInsurances).length
      ? normalizeSupplementaryInsurances(sourceForm.otherInsurances)
      : supplementaryInsurances,
    customCareQuestions: (snapshot.customQuestions || []).map((question, index) => ({
      id: `loaded-care-question-${index + 1}`,
      question: question.question || "",
      response: question.answer || question.response || "",
    })),
    documentFiles: withoutAssessmentPageDocuments(uploadedDocuments).map((document, index) => ({
      id: `loaded-document-${index + 1}`,
      ...document,
      needsUpload: false,
      uploaded: document.uploaded ?? Boolean(document.fileId || document.storageFileId),
    })),
    signedDocumentValidations: (documents.signedDocumentValidations || []).map((document, index) => ({
      id: `loaded-signed-document-${index + 1}`,
      ...document,
    })),
    onboardingStepStatus: preview.onboardingStepStatus || preview.previewPayload?.onboardingStepStatus || onboardingStepStatusFor(1),
    onboardingResidentId: preview.residentId || "",
    onboardingRegistrationId: preview.registrationId || "",
    assessmentProcessId: preview.assessmentProcessId || "",
  };
};

const normalizeExtractionText = (value = "") =>
  String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const inlineBioFieldsFromExtractionField = (field) => {
  const fields = [];
  const value = normalizeExtractionText(field.value);
  if (extractionLabelKey(field.label) === "resident" && /\bdate\s+of\s+birth\b/i.test(value)) {
    const sexPattern = sexOptions.map((option) => option.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
    const match = value.match(new RegExp(`^(.+?)\\s*\\|\\s*(${sexPattern})\\s*\\|\\s*Date of birth\\s+([^|]+)(?:\\s*\\|\\s*Age\\s+(.+))?$`, "i"));
    if (match) {
      fields.push({ ...field, label: "Resident Name", value: cleanMappedExtractionValue(match[1]) });
      fields.push({ ...field, label: "Client Gender", value: cleanMappedExtractionValue(match[2]) });
      fields.push({ ...field, label: "Date of birth", value: cleanMappedExtractionValue(match[3]) });
      if (match[4]) fields.push({ ...field, label: "Client Age", value: cleanMappedExtractionValue(match[4]) });
    }
  }
  if (extractionLabelKey(field.label) === "preferred name" && value.includes("|")) {
    const match = value.match(/^([^|]+)(?:\s*\|\s*Preferred language\s+([^|]+))?(?:\s*\|\s*Interpreter\s+(.+))?$/i);
    if (match) {
      fields.push({ ...field, label: "Preferred name", value: cleanMappedExtractionValue(match[1]) });
      if (match[2]) fields.push({ ...field, label: "Preferred language", value: cleanMappedExtractionValue(match[2]) });
      if (match[3]) fields.push({ ...field, label: "Interpreter", value: cleanMappedExtractionValue(match[3]) });
    }
  }
  return fields;
};

const extractionFieldsFrom = (extraction = {}) => {
  const fields = [];
  (Array.isArray(extraction.sections) ? extraction.sections : []).forEach((section) => {
    (Array.isArray(section.subsections) ? section.subsections : []).forEach((subsection) => {
      (Array.isArray(subsection.fields) ? subsection.fields : []).forEach((field) => {
        const label = normalizeExtractionText(field.label).replace(/[:：]+$/, "").trim();
        const value = normalizeExtractionText(field.value);
        if (!label || !value) return;
        fields.push({
          sectionKey: section.sectionKey || "",
          sectionTitle: section.title || "",
          subsectionTitle: subsection.title || "",
          label,
          value,
          confidence: Number(field.confidence || 0),
          reviewRequired: Boolean(field.reviewRequired),
        });
      });
    });
  });
  return [...fields, ...fields.flatMap(inlineBioFieldsFromExtractionField)];
};

const extractionLabelMatches = (field, patterns = [], sectionKeys = []) => {
  const label = field.label.toLowerCase();
  const sectionText = `${field.sectionKey} ${field.sectionTitle} ${field.subsectionTitle}`.toLowerCase();
  const sectionMatches = !sectionKeys.length || sectionKeys.includes(field.sectionKey);
  const searchableText = `${label} ${sectionText} ${String(field.value || "").toLowerCase()}`;
  return sectionMatches && patterns.some((pattern) => {
    pattern.lastIndex = 0;
    if (pattern.test(label)) return true;
    pattern.lastIndex = 0;
    return pattern.test(searchableText);
  });
};

const extractionFieldFor = (fields, patterns, sectionKeys = [], rejectPatterns = []) =>
  fields.find((field) => {
    const text = `${field.label} ${field.sectionTitle} ${field.subsectionTitle}`.toLowerCase();
    return extractionLabelMatches(field, patterns, sectionKeys) &&
      !rejectPatterns.some((pattern) => pattern.test(text));
  });

const extractionValueFor = (fields, patterns, sectionKeys = [], rejectPatterns = []) =>
  extractionFieldFor(fields, patterns, sectionKeys, rejectPatterns)?.value || "";

const extractionValuesFor = (fields, patterns, sectionKeys = []) =>
  fields.filter((field) => extractionLabelMatches(field, patterns, sectionKeys)).map((field) => field.value);

const extractionLabelKey = (value = "") =>
  normalizeExtractionText(value)
    .replace(/[:：]+$/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const extractionFieldByLabel = (fields = [], labels = [], rejectPatterns = []) => {
  const labelKeys = new Set(labels.map(extractionLabelKey).filter(Boolean));
  return fields.find((field) => {
    const text = `${field.label} ${field.sectionTitle} ${field.subsectionTitle}`.toLowerCase();
    return labelKeys.has(extractionLabelKey(field.label)) &&
      !rejectPatterns.some((pattern) => pattern.test(text));
  });
};

const extractionValueByLabel = (fields = [], labels = [], rejectPatterns = []) =>
  extractionFieldByLabel(fields, labels, rejectPatterns)?.value || "";

const cleanMappedExtractionValue = (value = "") =>
  normalizeExtractionText(value).replace(/\s*[|,;]+\s*$/g, "");

const addressFieldsFromExtractionText = (value = "") => {
  const text = cleanMappedExtractionValue(value);
  if (!text) return {};
  const parts = text.split(/\s*,\s*/).map((part) => part.trim()).filter(Boolean);
  const result = {};
  const last = parts[parts.length - 1] || "";
  const stateZip = last.match(/^(.+?)(?:\s+)(\d{5}(?:-\d{4})?)$/);
  if (stateZip) {
    const state = stateZip[1].trim();
    const stateAbbreviations = {
      AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
      CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho",
      IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
      ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
      MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
      NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
      OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
      TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
      WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
    };
    result.state = stateAbbreviations[state.toUpperCase()] || state;
    result.zip = stateZip[2];
    parts.pop();
  }
  if (parts.length >= 3) {
    result.address = parts[parts.length - 2];
    result.city = parts[parts.length - 1];
  } else if (parts.length === 2) {
    result.address = parts[0];
    result.city = parts[1];
  } else if (parts.length === 1) {
    result.address = parts[0];
  }
  return result;
};

const firstPipeSegment = (value = "") =>
  cleanMappedExtractionValue(String(value || "").split("|")[0]);

const extractionTextCorpus = (fields = []) =>
  fields.map((field) => `${field.label}: ${field.value}`).join("\n");

const embeddedExtractionValue = (fields = [], patterns = []) => {
  const text = extractionTextCorpus(fields);
  for (const pattern of patterns) {
    pattern.lastIndex = 0;
    const match = text.match(pattern);
    if (match?.[1]) {
      return cleanMappedExtractionValue(match[1]);
    }
  }
  return "";
};

const extractionFieldValue = (fields = [], labels = [], patterns = [], sectionKeys = [], rejectPatterns = []) =>
  cleanMappedExtractionValue(
    extractionValueByLabel(fields, labels, rejectPatterns) ||
    extractionValueFor(fields, patterns, sectionKeys, rejectPatterns),
  );

const residentProfileLineFromFields = (fields = []) => {
  const residentField = fields.find((field) =>
    extractionLabelKey(field.label) === "resident" &&
    /\bdate\s+of\s+birth\b/i.test(field.value) &&
    sexOptions.some((option) => new RegExp(`\\b${option.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i").test(field.value)));
  if (residentField?.value) {
    return cleanMappedExtractionValue(residentField.value);
  }
  return embeddedExtractionValue(fields, [
    /\bResident\s+([^.\n]+?\bDate\s+of\s+birth\b[^.\n]+)/i,
  ]);
};

const residentNameFromProfileLine = (line = "") =>
  firstPipeSegment(line)
    .replace(/\b(Female|Male|Intersex|Non-binary|Transgender female|Transgender male|Two-Spirit|Questioning|Unknown|Other)\b.*$/i, "")
    .trim();

const dateOfBirthFromProfileLine = (line = "") => {
  const match = normalizeExtractionText(line).match(/\bdate\s+of\s+birth\s+([^|]+)/i);
  return dateFromExtractionValue(match?.[1] || "");
};

const cleanUserFacingExtractionText = (value = "") =>
  normalizeExtractionText(value).replace(/\[REVIEW\]\s*/gi, "").trim();

const htmlFromLines = (lines = []) =>
  lines.filter(Boolean).map((line) => `<p>${cleanUserFacingExtractionText(line)}</p>`).join("");

const plainTextFromLines = (lines = []) =>
  lines.filter(Boolean).map((line) => cleanUserFacingExtractionText(line)).join("\n");

const plainTextFromHtml = (value = "") =>
  String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .trim();

const htmlListFromFields = (fields = [], maxItems = 24) =>
  fields.slice(0, maxItems)
    .map((field) => `<p><strong>${cleanUserFacingExtractionText(field.label)}:</strong> ${cleanUserFacingExtractionText(field.value)}</p>`)
    .join("");

const extractionSectionFields = (fields, sectionKeys = []) =>
  fields.filter((field) => sectionKeys.includes(field.sectionKey));

const extractionSectionSummary = (fields, sectionKeys = [], maxItems = 24) =>
  htmlListFromFields(extractionSectionFields(fields, sectionKeys), maxItems);

const dateFromExtractionValue = (value = "") => {
  const text = normalizeExtractionText(value);
  const iso = text.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (iso) return iso[0];
  const us = text.match(/\b(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})\b/);
  if (us) {
    const [, month, day, year] = us;
    const fullYear = year.length === 2 ? `19${year}` : year;
    return `${fullYear}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  const namedMonth = text.match(/\b(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})\b/) ||
    text.match(/\b([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})\b/);
  if (!namedMonth) return "";
  if (/^\d/.test(namedMonth[1])) {
    const [, day, monthName, year] = namedMonth;
    const month = MONTHS_BY_NAME[monthName.toLowerCase()];
    return month ? `${year}-${month}-${day.padStart(2, "0")}` : "";
  }
  const [, monthName, day, year] = namedMonth;
  const month = MONTHS_BY_NAME[monthName.toLowerCase()];
  return month ? `${year}-${month}-${day.padStart(2, "0")}` : "";
};

const yesNoFromExtractionValue = (value = "") => {
  const text = normalizeExtractionText(value).toLowerCase();
  if (/\b(yes|true|checked|has|present)\b/.test(text)) return "Yes";
  if (/\b(no|false|none|not applicable|n\/a)\b/.test(text)) return "No";
  return "";
};

const codeStatusFromExtractionValue = (value = "") => {
  const text = normalizeExtractionText(value).toLowerCase();
  if (!text) return "";
  if (/\bdnr\b|do not resuscitate/.test(text)) return "DNR";
  if (/comfort care/.test(text)) return "Comfort Care";
  if (/full code|attempt cpr|cpr/.test(text)) return "Full Code";
  if (/unknown|pending|not indicated/.test(text)) return "Unknown";
  return codeStatusOptions.find((option) => option.toLowerCase() === text) || "";
};

const PHONE_PATTERN = /(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/;
const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const MONTHS_BY_NAME = {
  january: "01",
  jan: "01",
  february: "02",
  feb: "02",
  march: "03",
  mar: "03",
  april: "04",
  apr: "04",
  may: "05",
  june: "06",
  jun: "06",
  july: "07",
  jul: "07",
  august: "08",
  aug: "08",
  september: "09",
  sep: "09",
  sept: "09",
  october: "10",
  oct: "10",
  november: "11",
  nov: "11",
  december: "12",
  dec: "12",
};
const RESIDENT_FIELD_REJECT_PATTERNS = [
  /provider/,
  /facility/,
  /license/,
  /capacity/,
  /survey/,
  /assessor/,
  /clinician/,
  /case manager/,
  /office/,
  /pharmacy/,
  /physician/,
  /guardian/,
  /representative/,
  /contact/,
  /emergency/,
];
const CURRENT_RESIDENT_CONTACT_REJECT_PATTERNS = [
  ...RESIDENT_FIELD_REJECT_PATTERNS,
  /previous/,
  /prior/,
  /former/,
  /history/,
  /admission/,
  /hospital/,
  /source/,
  /placement/,
  /family home/,
  /household/,
];
const RESIDENT_NAME_REJECT_PATTERNS = [
  /assessor/,
  /case manager/,
  /office/,
  /provider/,
  /caregiver/,
  /facility/,
  /family home/,
  /afh/,
  /guardian/,
  /representative/,
  /attorney/,
  /physician/,
  /pharmacy/,
  /contact/,
  /relationship/,
];
const GENERIC_CONTACT_LABEL_PATTERN = /^(phone|telephone|mobile|name|relationship|email|address|office|assessment date|client aces id)$/i;

const isUsefulContactValue = (value = "") => {
  const text = normalizeExtractionText(value);
  if (!text || /^(yes|no|none|n\/a|not related)$/i.test(text)) return false;
  if (/^\d{1,4}$/.test(text)) return false;
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(text)) return false;
  return PHONE_PATTERN.test(text) || EMAIL_PATTERN.test(text) || /[A-Za-z]{3,}/.test(text);
};

const contactRoleForField = (field) => {
  const label = `${field.label} ${field.subsectionTitle} ${field.sectionTitle}`.toLowerCase();
  const value = String(field.value || "").toLowerCase();
  const text = `${label} ${value}`;
  if (/(emergency classification|code status|polst|directive|fall risk|wristband)/.test(label)) return "";
  if (/(power of att|poa|representative|protective payee)/.test(text)) return "Power of Attorney";
  if (/guardian/.test(text)) return "Legal Guardian";
  if (/primary emergency contact|emergency contact/.test(label)) return "Emergency Contact";
  if (/(case manager|social worker|care coordinator|current case manager)/.test(text)) return "Social Worker / Case Manager";
  if (/(primary physician|primary care|doctor|physician|pcp)/.test(text)) return "Primary Care Doctor";
  if (/consultant|specialist|neurology|neurologist/.test(text)) return "Specialist";
  if (/pharmacy/.test(text)) return "Pharmacy";
  if (/sibling|sister|brother/.test(text)) return "Sibling";
  if (/daughter/.test(text)) return "Daughter";
  if (/\bson\b/.test(text)) return "Son";
  if (/spouse|wife|husband/.test(text)) return "Spouse";
  if (/family|relative/.test(text)) return "Other";
  return "";
};

const relationshipFromContactText = (value = "", role = "") => {
  const text = normalizeExtractionText(value).toLowerCase();
  if (role === "Pharmacy" || role === "Primary Care Doctor" || role === "Specialist") return role;
  if (/daughter/.test(text)) return "Daughter";
  if (/\bson\b/.test(text)) return "Son";
  if (/spouse|wife|husband/.test(text)) return "Spouse";
  if (/sibling|sister|brother/.test(text)) return "Sibling";
  if (/guardian/.test(text)) return "Legal Guardian";
  if (/power of att|poa|representative|protective payee/.test(text)) return "Power of Attorney";
  if (/case manager/.test(text)) return "Case Manager";
  if (/social worker/.test(text)) return "Social Worker / Case Manager";
  if (/pharmacy/.test(text) || role === "Pharmacy") return "Pharmacy";
  if (/doctor|physician|md\b/.test(text) || role === "Primary Care Doctor") return "Primary Care Doctor";
  if (/specialist|consultant|neurology/.test(text) || role === "Specialist") return "Specialist";
  return role || "Other";
};

const contactNameFromField = (field, role, phone, email) => {
  const text = normalizeExtractionText(field.value)
    .replace(phone || "", "")
    .replace(email || "", "")
    .replace(/\b(Mobile|Office|Home|Phone|Relationship|Not related|Other family|Sibling|Daughter|Son|Spouse|First call|Second call|Lives? \d+ miles away|Secure fax [\d\s().-]+)\b:?/gi, " ")
    .replace(/\b(MD|RN|LPN|BSN)\b\.?/g, " ")
    .split("|")[0]
    .replace(/[,;]+/g, " ")
    .trim();
  const candidates = text.split(/\s{2,}| - | \| /).map(normalizeExtractionText).filter(Boolean);
  const best = candidates.find((candidate) =>
    /[A-Za-z]{3,}/.test(candidate) &&
    !/^(yes|no|none|n\/a|not related)$/i.test(candidate) &&
    candidate.toLowerCase() !== role.toLowerCase());
  return best || text;
};

const residentNameFromFields = (fields = []) => {
  const profileLine = residentProfileLineFromFields(fields);
  const profileName = residentNameFromProfileLine(profileLine);
  if (profileName) {
    return profileName;
  }
  const exactValue = extractionValueByLabel(fields, [
    "Client Name",
    "Resident Name",
    "Client Full Name",
    "Resident Full Name",
  ], RESIDENT_NAME_REJECT_PATTERNS);
  if (exactValue) {
    return cleanMappedExtractionValue(exactValue);
  }
  const field = extractionFieldFor(
    fields,
    [/^(client|resident)\s*name$/i, /\b(client|resident)\s+full\s+name\b/i],
    [],
    RESIDENT_NAME_REJECT_PATTERNS,
  );
  if (field?.value) {
    return cleanMappedExtractionValue(field.value);
  }
  return embeddedExtractionValue(fields, [
    /\b(?:Client|Resident)\s+([A-Z][A-Za-z'-]+(?:\s+[A-Z][A-Za-z'-]+){1,4})\s*(?:\||,|\n|$)/,
  ]);
};

const conciseFieldsForPlan = (fields = [], patterns = [], sectionKeys = [], maxItems = 8) =>
  fields
    .filter((field) => extractionLabelMatches(field, patterns, sectionKeys))
    .filter((field) => !GENERIC_CONTACT_LABEL_PATTERN.test(field.label))
    .filter((field) => normalizeExtractionText(field.value).length >= 3)
    .slice(0, maxItems);

const distinctContacts = (entries = []) => {
  const seen = new Set();
  return entries.filter(Boolean).filter((entry) => {
    const key = [
      entry.contact.type,
      entry.formData[contactField(entry.contact.prefix, "name")],
      entry.formData[contactField(entry.contact.prefix, "organizationName")],
      entry.formData[contactField(entry.contact.prefix, "mobilePhone")],
      entry.formData[contactField(entry.contact.prefix, "email")],
    ].map((value) => String(value || "").toLowerCase().trim()).join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const contactFromExtractionField = (field, index) => {
  const role = contactRoleForField(field);
  if (!role || GENERIC_CONTACT_LABEL_PATTERN.test(field.label) || !isUsefulContactValue(field.value)) {
    return null;
  }
  const prefix = `residentContactImported${Date.now()}${index + 1}`;
  const value = contactValueForField(field);
  const phone = value.match(PHONE_PATTERN)?.[0] || "";
  const email = field.value.match(EMAIL_PATTERN)?.[0] || "";
  if (!phone && !email) {
    return null;
  }
  const cleanField = { ...field, value };
  const name = contactNameFromField(cleanField, role, phone, email);
  const relationship = relationshipFromContactText(value, role);
  const useOfficePhone = ["Primary Care Doctor", "Specialist", "Pharmacy"].includes(relationship);
  const notes = value
    .split("|")
    .map((part) => part.trim())
    .filter((part) => {
      const normalizedPart = part.toLowerCase();
      if (!part) return false;
      if (name && normalizedPart.includes(name.toLowerCase())) return false;
      if (phone && part.replace(/\D/g, "").includes(phone.replace(/\D/g, ""))) return false;
      if (email && normalizedPart.includes(email.toLowerCase())) return false;
      if (normalizedPart === relationship.toLowerCase()) return false;
      return true;
    })
    .join(" | ");
  return {
    contact: {
      id: `imported-contact-${Date.now()}-${index + 1}`,
      prefix,
      type: relationship,
      relationshipOther: relationship === "Other" ? role : "",
      notes,
    },
    formData: {
      [contactField(prefix, "name")]: name || field.value,
      [contactField(prefix, "organizationName")]: "",
      [contactField(prefix, "mobilePhone")]: useOfficePhone ? "" : phone,
      [contactField(prefix, "officePhone")]: useOfficePhone ? phone : "",
      [contactField(prefix, "homePhone")]: "",
      [contactField(prefix, "preferredPhone")]: phone ? (useOfficePhone ? "officePhone" : "mobilePhone") : "",
      [contactField(prefix, "email")]: email,
      [contactField(prefix, "relationship")]: relationship,
    },
  };
};

const contactValueForField = (field = {}) => {
  const value = normalizeExtractionText(field.value);
  const label = extractionLabelKey(field.label);
  const stopLabels = [
    "medications",
    "current medications",
    "diagnosis",
    "allergies",
    "diet",
    "caregiver instructions",
    "caregiver instruction",
    "finances",
    "iadl narrative",
    "record integrity",
    "page ",
    "state healthcare compliance",
  ];
  let end = value.length;
  stopLabels.forEach((stopLabel) => {
    const index = value.toLowerCase().indexOf(stopLabel.toLowerCase());
    if (index > 0) end = Math.min(end, index);
  });
  const trimmed = cleanMappedExtractionValue(value.slice(0, end));
  if (label === "pharmacy") {
    const phone = trimmed.match(PHONE_PATTERN)?.[0] || "";
    const beforeFax = trimmed.split(/secure fax/i)[0];
    return cleanMappedExtractionValue(beforeFax || (phone ? trimmed.slice(0, trimmed.indexOf(phone) + phone.length) : trimmed));
  }
  return trimmed;
};

const embeddedContactFieldsFrom = (fields = []) => {
  const text = extractionTextCorpus(fields).replace(/\s+/g, " ");
  const labelPattern = /\b(Primary emergency contact|Secondary contact|Attending physician|Neurology consultant|Pharmacy)\b\s*:?\s*/gi;
  const matches = [];
  let match = labelPattern.exec(text);
  while (match) {
    matches.push({
      label: match[1],
      start: match.index,
      valueStart: labelPattern.lastIndex,
    });
    match = labelPattern.exec(text);
  }
  return matches.map((current, index) => {
    const next = matches[index + 1];
    const value = contactValueForField({
      label: current.label,
      value: cleanMappedExtractionValue(text.slice(current.valueStart, next ? next.start : current.valueStart + 260)),
    });
    return {
      sectionKey: "client_profile",
      sectionTitle: "Client Profile",
      subsectionTitle: "Embedded contacts",
      label: current.label,
      value,
      confidence: 0.7,
      reviewRequired: false,
    };
  }).filter((field) => PHONE_PATTERN.test(field.value));
};

const onboardingImportFromAssessmentExtraction = (extraction = {}) => {
  const fields = extractionFieldsFrom(extraction);
  const importedForm = {};
  const residentProfileLine = residentProfileLineFromFields(fields);
  const fullName = residentNameFromFields(fields);
  Object.assign(importedForm, splitName(fullName));
  importedForm.preferredName = cleanMappedExtractionValue(
    firstPipeSegment(extractionValueByLabel(fields, ["Client Preferred Name", "Resident Preferred Name", "Preferred Name", "Nickname"], RESIDENT_FIELD_REJECT_PATTERNS)) ||
    extractionValueFor(fields, [/\b(client|resident)\s+preferred\s+name\b/i, /^preferred\s+name$/i, /nickname/i], [], RESIDENT_FIELD_REJECT_PATTERNS) ||
    embeddedExtractionValue(fields, [/\bPreferred\s+name\s+([^|\n]+)/i]),
  );
  importedForm.dateOfBirth = dateFromExtractionValue(
    extractionValueByLabel(fields, ["Client Date of Birth", "Resident Date of Birth", "Date of Birth", "DOB", "Birth Date"], RESIDENT_FIELD_REJECT_PATTERNS) ||
    extractionValueFor(fields, [/\b(client|resident)\s+date\s+of\s+birth\b/i, /^date.*birth$/i, /\bdob\b/i, /birth.*date/i], [], RESIDENT_FIELD_REJECT_PATTERNS),
  ) || dateOfBirthFromProfileLine(residentProfileLine);
  importedForm.sex = displaySexValue(
    embeddedExtractionValue([{ label: "Resident", value: residentProfileLine }], [
      /\b(?:Client|Resident)\s*:?\s*[A-Z][A-Za-z'-]+(?:\s+[A-Z][A-Za-z'-]+){1,4}\s*\|\s*(Female|Male|Intersex|Non-binary|Transgender female|Transgender male|Two-Spirit|Questioning|Unknown|Other)\b/i,
    ]) ||
    extractionValueByLabel(fields, ["Client Gender", "Resident Gender", "Client Sex", "Resident Sex", "Gender", "Sex"], RESIDENT_FIELD_REJECT_PATTERNS) ||
    extractionValueFor(fields, [/\b(client|resident)\s+(sex|gender)\b/i, /^(sex|gender)$/i], [], RESIDENT_FIELD_REJECT_PATTERNS) ||
    embeddedExtractionValue(fields, [
      /\b(?:Client|Resident)\s*:?\s*[A-Z][A-Za-z'-]+(?:\s+[A-Z][A-Za-z'-]+){1,4}\s*\|\s*(Female|Male|Intersex|Non-binary|Transgender female|Transgender male|Two-Spirit|Questioning|Unknown|Other)\b/i,
    ]),
  );
  importedForm.socialSecurityNumber = formatSocialSecurityNumber(
    extractionValueByLabel(fields, ["Social Security Number", "SSN", "Client Social Security Number", "Resident Social Security Number"], RESIDENT_FIELD_REJECT_PATTERNS) ||
    extractionValueFor(fields, [/social.*security/i, /\bssn\b/i], [], RESIDENT_FIELD_REJECT_PATTERNS),
  );
  importedForm.primaryLanguage = extractionValueByLabel(fields, ["Client Preferred Language", "Resident Preferred Language", "Preferred Language", "Language"], RESIDENT_FIELD_REJECT_PATTERNS) ||
    extractionValueFor(fields, [/\b(client|resident)\s+preferred\s+language\b/i, /^preferred\s+language$/i, /^language$/i, /interpreter/i], [], RESIDENT_FIELD_REJECT_PATTERNS);
  importedForm.race = extractionFieldValue(fields, ["Client Race", "Resident Race", "Race", "Ethnicity"], [/\b(client|resident)\s+(race|ethnicity)\b/i, /^(race|ethnicity)$/i], ["client_profile"], RESIDENT_FIELD_REJECT_PATTERNS);
  importedForm.maritalStatus = extractionFieldValue(fields, ["Client Marital Status", "Resident Marital Status", "Marital Status"], [/marital\s+status/i], ["client_profile"], RESIDENT_FIELD_REJECT_PATTERNS);
  importedForm.placeOfBirth = extractionFieldValue(fields, ["Client Place of Birth", "Resident Place of Birth", "Place of Birth", "Birthplace"], [/place\s+of\s+birth/i, /birthplace/i], ["client_profile"], RESIDENT_FIELD_REJECT_PATTERNS);
  importedForm.religion = extractionFieldValue(fields, ["Client Religion", "Resident Religion", "Religion", "Religious Preference"], [/religion|religious\s+preference/i], ["client_profile"], RESIDENT_FIELD_REJECT_PATTERNS);
  const extractedHeight = extractionFieldValue(fields, ["Client Height", "Resident Height", "Height"], [/\bheight\b/i], ["client_profile"], RESIDENT_FIELD_REJECT_PATTERNS);
  const heightMatch = extractedHeight.match(/(\d+)\s*(?:ft|feet|')?\s*(\d+)?/i);
  if (heightMatch) { importedForm.heightFeet = heightMatch[1]; importedForm.heightInches = heightMatch[2] || ""; }
  const extractedWeight = extractionFieldValue(fields, ["Client Weight", "Resident Weight", "Weight"], [/\bweight\b/i], ["client_profile"], RESIDENT_FIELD_REJECT_PATTERNS);
  importedForm.weightLbs = extractedWeight.match(/\d+(?:\.\d+)?/)?.[0] || "";
  const codeStatusSource = extractionValueByLabel(fields, ["Code Status", "Emergency Classification", "POLST Directives", "POLST"]) ||
    extractionValueFor(fields, [/code.*status/i, /emergency.*classification/i, /\bdnr\b/i, /\bcpr\b/i], ["client_profile", "health_services"]);
  importedForm.codeStatus = codeStatusFromExtractionValue(
    codeStatusSource,
  );
  const advancedDirectivesSource = extractionValueByLabel(fields, [
    "Advanced Directives",
    "Advance Directives",
    "Directive",
    "POLST Directives",
    "POLST",
    "Living will",
    "Durable Power of Attorney",
  ]) || extractionValueFor(fields, [/advanced.*directive/i, /living will/i, /durable power of attorney/i, /directive/i, /polst/i], ["client_profile", "health_services"]);
  importedForm.advancedDirectives = yesNoFromExtractionValue(
    advancedDirectivesSource,
  ) || (advancedDirectivesSource ? "Yes" : "");
  importedForm.medicareNumber = extractionValueByLabel(fields, ["Medicare Number", "Client Medicare Number", "Resident Medicare Number"], RESIDENT_FIELD_REJECT_PATTERNS) ||
    extractionValueFor(fields, [/medicare/i], ["client_profile", "health_services"], RESIDENT_FIELD_REJECT_PATTERNS);
  importedForm.medicaidNumber = extractionValueByLabel(fields, ["Medicaid Number", "Client Medicaid Number", "Resident Medicaid Number"], RESIDENT_FIELD_REJECT_PATTERNS) ||
    extractionValueFor(fields, [/medicaid/i], ["client_profile", "health_services"], RESIDENT_FIELD_REJECT_PATTERNS);
  importedForm.email = extractionFieldFor(fields, [/\b(client|resident)\s+email\b/i, /^email$/i], [], CURRENT_RESIDENT_CONTACT_REJECT_PATTERNS)?.value || "";
  importedForm.mobilePhone = extractionFieldFor(fields, [/\b(client|resident)\s+(phone|telephone|mobile)\b/i, /^(phone|telephone|mobile)$/i], [], CURRENT_RESIDENT_CONTACT_REJECT_PATTERNS)?.value || "";
  importedForm.address = extractionValueFor(fields, [/^(client|resident)\s+(current\s+)?address$/i, /^(current\s+)?address$/i], [], CURRENT_RESIDENT_CONTACT_REJECT_PATTERNS);
  importedForm.city = extractionValueFor(fields, [/^(client|resident)\s+(current\s+)?city$/i, /^(current\s+)?city$/i], [], CURRENT_RESIDENT_CONTACT_REJECT_PATTERNS);
  importedForm.county = extractionValueFor(fields, [/^(client|resident)\s+(current\s+)?county$/i, /^(current\s+)?county$/i], [], CURRENT_RESIDENT_CONTACT_REJECT_PATTERNS);
  importedForm.state = extractionValueFor(fields, [/^(client|resident)\s+(current\s+)?state$/i, /^(current\s+)?state$/i], [], CURRENT_RESIDENT_CONTACT_REJECT_PATTERNS);
  importedForm.zip = extractionValueFor(fields, [/^(client|resident)\s+(current\s+)?(zip|postal)/i, /^(current\s+)?(zip|postal)/i], [], CURRENT_RESIDENT_CONTACT_REJECT_PATTERNS);
  importedForm.country = extractionValueFor(fields, [/^(client|resident)\s+(current\s+)?country$/i, /^(current\s+)?country$/i], [], CURRENT_RESIDENT_CONTACT_REJECT_PATTERNS);
  importedForm.bioNotes = extractionSectionSummary(fields, ["client_profile"], 18);

  const hospitalFields = extractionValuesFor(fields, [/hospital|hospitalization|admission|discharge|emergency room|\bER\b/i], [], 20);
  const hospitalText = fields
    .filter((field) => /hospital|hospitalization|admission|discharge|emergency room|\bER\b/i.test(`${field.label} ${field.value}`))
    .map((field) => `${field.label}: ${field.value}`)
    .join(" | ");
  const admissionValue = extractionFieldValue(
    fields,
    ["Admission Date", "Hospital Admission Date", "Date of Admission"],
    [/admission\s+date|date\s+of\s+admission/i],
    [],
  ) || hospitalText.match(/(?:admission\s+date|date\s+of\s+admission)\s*[:|-]?\s*([^|]+)/i)?.[1] || "";
  importedForm.hospitalName = cleanMappedExtractionValue(extractionFieldValue(
    fields,
    ["Hospital Name", "Hospital", "Facility Name"],
    [/hospital\s+name|hospital|facility/i],
    [],
  ));
  const hospitalAddress = addressFieldsFromExtractionText(importedForm.hospitalName);
  if (hospitalAddress.address) {
    importedForm.hospitalName = cleanMappedExtractionValue(importedForm.hospitalName.split(/\s*,\s*/)[0]);
    Object.entries(hospitalAddress).forEach(([field, value]) => {
      importedForm[`hospital${field[0].toUpperCase()}${field.slice(1)}`] = value;
    });
  }
  importedForm.hospitalContactName = cleanMappedExtractionValue(extractionFieldValue(fields, ["Hospital Contact", "Hospital Contact Name"], [/hospital.*contact|contact.*hospital/i], []));
  importedForm.hospitalPhone = extractionFieldValue(fields, ["Hospital Phone"], [/hospital.*phone|hospital.*telephone/i], []);
  importedForm.hospitalEmail = extractionFieldValue(fields, ["Hospital Email"], [/hospital.*email/i], []);
  importedForm.admissionDate = dateFromExtractionValue(extractionFieldValue(
    fields,
    ["Admission Date"],
    [/^admission\s+date$/i],
    [],
  ) || admissionValue);
  importedForm.dischargeDate = dateFromExtractionValue(extractionFieldValue(
    fields,
    ["Discharge Date", "Date of Discharge"],
    [/discharge\s+date|date\s+of\s+discharge/i],
    [],
  ));
  importedForm.wasAdmittedAtHospital = hospitalFields.length ? "Yes" : "";
  importedForm.admissionDescription = htmlFromLines([
    extractionFieldValue(fields, ["Reason for Admission"], [/reason.*admission/i], []),
    extractionFieldValue(fields, ["Initial Impression"], [/initial.*impression/i], []),
    ...hospitalFields,
  ]);
  const previousAddress = extractionFieldValue(fields, ["Previous Address", "Client Previous Address"], [/previous.*address/i], []);
  const household = extractionFieldValue(fields, ["Household", "Prior Household Status"], [/household|prior.*living|previous.*living/i], []);
  const supportBeforeAdmission = extractionFieldValue(fields, ["Support Before Admission"], [/support.*before.*admission/i], []);
  const familyHomeFields = extractionValuesFor(fields, [/family home|adult family home|previous home|prior home|placement|residential setting/i], [], 20);
  importedForm.priorFamilyHomeName = cleanMappedExtractionValue(extractionFieldValue(fields, ["Family Home Name", "Previous Family Home", "Prior Family Home"], [/family home.*name|previous family home|prior family home/i], []));
  importedForm.priorFamilyHomeContactName = cleanMappedExtractionValue(extractionFieldValue(fields, ["Family Home Contact", "Family Home Contact Name"], [/family home.*contact|contact.*family home/i], []));
  importedForm.priorFamilyHomePhone = extractionFieldValue(fields, ["Family Home Phone"], [/family home.*phone|previous home.*phone/i], []);
  importedForm.priorFamilyHomeEmail = extractionFieldValue(fields, ["Family Home Email"], [/family home.*email|previous home.*email/i], []);
  importedForm.fromAnotherFamilyHome = familyHomeFields.length || previousAddress || household ? "Yes" : "";
  importedForm.priorFamilyHomeDetails = plainTextFromLines([
    previousAddress ? `Previous address: ${previousAddress}` : "",
    household,
    ...familyHomeFields,
  ]);
  const priorFamilyHomeAddress = addressFieldsFromExtractionText(previousAddress);
  Object.entries(priorFamilyHomeAddress).forEach(([field, value]) => {
    importedForm[`priorFamilyHome${field[0].toUpperCase()}${field.slice(1)}`] = value;
  });
  const priorFamilyHomeYears = String(importedForm.priorFamilyHomeDetails || "").match(/(\d+)\s+years?/i);
  if (priorFamilyHomeYears) importedForm.priorFamilyHomeYears = priorFamilyHomeYears[1];
  const caregiverFields = extractionValuesFor(fields, [/caregiver|care provider|support person|home care|personal care assistant/i], [], 20);
  importedForm.priorCaregiverName = cleanMappedExtractionValue(extractionFieldValue(fields, ["Caregiver Name", "Prior Caregiver", "Previous Caregiver"], [/caregiver.*name|prior caregiver|previous caregiver/i], []));
  importedForm.priorCaregiverRelationship = cleanMappedExtractionValue(extractionFieldValue(fields, ["Caregiver Relationship"], [/caregiver.*relationship|relationship.*caregiver/i], []));
  importedForm.priorCaregiverPhone = extractionFieldValue(fields, ["Caregiver Phone"], [/caregiver.*phone|caregiver.*telephone/i], []);
  importedForm.priorCaregiverEmail = extractionFieldValue(fields, ["Caregiver Email"], [/caregiver.*email/i], []);
  importedForm.hadPriorCaregiver = supportBeforeAdmission || caregiverFields.length ? "Yes" : "";
  importedForm.priorCaregiverDetails = plainTextFromLines([
    supportBeforeAdmission,
    ...caregiverFields,
  ]);
  const caregiverRelationship = String(importedForm.priorCaregiverDetails || "").match(/\b(daughter|son|spouse|partner|sibling|neighbor|friend|home health aide|caregiver)\b/i);
  if (caregiverRelationship) importedForm.priorCaregiverRelationship = caregiverRelationship[1];

  const diagnosisFields = fields.filter((field) => extractionLabelMatches(field, [/diagnos|condition|medical history|health history|problem list/i], []));
  const allergyFields = fields.filter((field) => extractionLabelMatches(field, [/allerg/i], []));
  const dietFields = fields.filter((field) => extractionLabelMatches(field, [/diet|nutrition|meal|food preference|feeding|swallow/i], []));
  const medicationFields = fields.filter((field) => extractionLabelMatches(field, [/medication|prescription|drug|regimen/i], []));
  const careNeedFields = fields.filter((field) => extractionLabelMatches(field, [
    /assistance|supervision|support|care need|daily living|mobility|transfer|continence|personal care|nutrition|diet|transport|toileting|bathing|dressing|feeding/i,
  ], []));
  const behaviorFields = fields.filter((field) => extractionLabelMatches(field, [
    /behavior|behaviour|safety|communication|memory|sleep|fall|elopement|choking|seizure|agitation/i,
  ], []));
  importedForm.diagnosis = htmlListFromFields(diagnosisFields, 18) || extractionSectionSummary(fields, ["mental_physical_health", "health_services"], 18);
  importedForm.allergies = htmlListFromFields(allergyFields, 12) || extractionSectionSummary(fields, ["health_services", "medications"], 12);
  importedForm.diet = htmlListFromFields(dietFields, 12) || extractionSectionSummary(fields, ["nutrition_diet"], 12);
  importedForm.medicationNotes = htmlListFromFields(medicationFields, 24) || extractionSectionSummary(fields, ["medications"], 24);
  importedForm.careNeeded = htmlListFromFields(careNeedFields, 30) || extractionSectionSummary(fields, [
    "activities_daily_living", "mobility_transfers", "continence", "personal_care", "skin_foot_care",
    "instrumental_daily_living", "nutrition_diet", "transportation",
  ], 30);
  importedForm.behaviorSafetyNotes = htmlListFromFields(behaviorFields, 28) || extractionSectionSummary(fields, [
    "safety_communication", "sleep_memory", "behaviour_emotional",
  ], 28);

  const summary = extraction.caregiverSummary || {};
  importedForm.preliminaryPlanGoals = htmlListFromFields(conciseFieldsForPlan(fields, [/goal|preference|routine|interest|important|resident wants|resident likes/i], [], 8));
  importedForm.preliminaryPlanCareApproach = htmlFromLines([
    ...(summary.immediatePriorities || []),
  ]);
  if (!importedForm.preliminaryPlanCareApproach) {
    importedForm.preliminaryPlanCareApproach = htmlListFromFields(conciseFieldsForPlan(fields, [/assistance|supervision|support|routine|care need|how to support/i], [], 8));
  }
  importedForm.preliminaryPlanStaffInstructions = htmlFromLines([
    ...(summary.requiredAssistance || []),
    ...(summary.reportingInstructions || []),
  ]);
  if (!importedForm.preliminaryPlanStaffInstructions) {
    importedForm.preliminaryPlanStaffInstructions = htmlListFromFields(conciseFieldsForPlan(fields, [/medication|transfer|monitor|report|notify|assist|supervision|staff|caregiver instruction/i], [], 8));
  }
  importedForm.preliminaryPlanFamilyPreferences = htmlListFromFields(conciseFieldsForPlan(fields, [/family|representative|guardian|poa|visit|preference|important to resident/i], [], 8));
  importedForm.preliminaryPlanRisksAndFollowUp = htmlFromLines([
    ...(summary.risksAndWarnings || []),
    ...((extraction.warnings || []).slice(0, 4)),
  ]);
  if (!importedForm.preliminaryPlanRisksAndFollowUp) {
    importedForm.preliminaryPlanRisksAndFollowUp = htmlListFromFields(conciseFieldsForPlan(fields, [/risk|safety|fall|elopement|choking|allergy|seizure|skin|behavior|behaviour|follow.?up/i], [], 8));
  }

  const contactFields = [...fields, ...embeddedContactFieldsFrom(fields)]
    .filter((field) => contactRoleForField(field))
    .filter((field) => extractionLabelMatches(field, [/emergency|guardian|poa|power of attorney|representative|protective payee|case manager|social worker|doctor|physician|pcp|consultant|specialist|neurology|neurologist|pharmacy|sibling|sister|brother|daughter|son|spouse/i], [
      "client_profile",
      "health_services",
      "provider_information",
    ]))
    .filter((field) => !GENERIC_CONTACT_LABEL_PATTERN.test(field.label))
    .slice(0, 12);
  const contactEntries = distinctContacts(contactFields.map(contactFromExtractionField)).slice(0, 8);
  return {
    formData: Object.fromEntries(Object.entries(importedForm).filter(([, value]) => String(value || "").trim())),
    contacts: contactEntries.map((entry) => entry.contact),
    contactEntries,
    contactFormData: contactEntries.reduce((merged, entry) => ({ ...merged, ...entry.formData }), {}),
    customCareQuestions: [
      ...fields.filter((field) => field.reviewRequired).slice(0, 6).map((field, index) => ({
        id: `imported-review-question-${Date.now()}-${index + 1}`,
        question: `Review ${field.label}`,
        response: field.value,
      })),
    ],
  };
};

const mergeImportedFormData = (current = {}, imported = {}) => {
  const next = { ...current };
  Object.entries(imported).forEach(([key, value]) => {
    if (value !== null && value !== undefined && String(value).trim()) {
      next[key] = value;
    }
  });
  return next;
};

const replaceImportedFormData = (current = {}, imported = {}) => {
  const next = { ...current };
  Object.keys(next).forEach((field) => {
    if (field.startsWith("residentContactImported")) {
      delete next[field];
    }
  });
  return mergeImportedFormData(next, imported);
};

const importPreviewValue = (value = "") =>
  normalizeExtractionText(String(value || "").replace(/<br\s*\/?>/gi, " ").replace(/<\/p>/gi, " "));

const importedContactName = (entry = {}) =>
  entry.formData?.[contactField(entry.contact?.prefix, "name")] ||
  entry.formData?.[contactField(entry.contact?.prefix, "organizationName")] ||
  entry.contact?.notes || "";

const importedPrefillGroups = (imported = {}, extraction = {}) => {
  const form = imported.formData || {};
  const contactEntries = imported.contactEntries || [];
  const fieldRows = (fields = []) => fields
    .map(([field, label]) => [label, importPreviewValue(form[field])])
    .filter(([, value]) => value);
  return [
    {
      title: "Resident Identifiers",
      rows: [
        ...fieldRows(bioFields.slice(0, 6)),
        ["Sex", importPreviewValue(form.sex)],
        ["Code Status", importPreviewValue(form.codeStatus)],
        ["Advanced Directives", importPreviewValue(form.advancedDirectives)],
      ].filter(([, value]) => value),
    },
    {
      title: "Resident Contact and Insurance",
      rows: [
        ...fieldRows(phoneFields),
        ["Preferred phone", preferredPhoneLabel(form.preferredPhone)],
        ["Email", importPreviewValue(form.email)],
        ...fieldRows([
          ["address", "Address line 1"],
          ["city", "City"],
          ["county", "County"],
          ["state", "State"],
          ["zip", "Zip code"],
          ["country", "Country"],
        ]),
        ...fieldRows(insuranceFields),
      ].filter(([, value]) => value),
    },
    {
      title: "Contacts and Care Team",
      rows: contactEntries
        .map((entry, index) => [
          entry.contact?.type || `Contact ${index + 1}`,
          importPreviewValue([
            importedContactName(entry),
            entry.formData?.[contactField(entry.contact?.prefix, "mobilePhone")],
            entry.formData?.[contactField(entry.contact?.prefix, "email")],
          ].filter(Boolean).join(" | ")),
        ])
        .filter(([, value]) => value),
    },
    {
      title: "Care Snapshot",
      rows: [
        ["Diagnosis", form.diagnosis],
        ["Allergies", form.allergies],
        ["Diet", form.diet],
        ["Medication notes", form.medicationNotes],
        ["Care needed", form.careNeeded],
        ["Behavior and safety notes", form.behaviorSafetyNotes],
      ].map(([label, value]) => [label, importPreviewValue(value)])
        .filter(([, value]) => value),
    },
    {
      title: "Preliminary Plan",
      rows: [
        ["Resident goals", form.preliminaryPlanGoals],
        ["Care approach", form.preliminaryPlanCareApproach],
        ["Staff instructions", form.preliminaryPlanStaffInstructions],
        ["Family preferences", form.preliminaryPlanFamilyPreferences],
        ["Risks and follow-up", form.preliminaryPlanRisksAndFollowUp],
      ].map(([label, value]) => [label, importPreviewValue(value)])
        .filter(([, value]) => value),
    },
    {
      title: "Extraction Review",
      rows: [
        ["Fields found", extractionFieldsFrom(extraction).length],
        ["Review questions", imported.customCareQuestions?.length || 0],
      ].map(([label, value]) => [label, String(value)])
        .filter(([, value]) => value),
    },
  ].filter((group) => group.rows.length);
};

const dateInputValueFrom = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : text;
};

const firstBioValue = (...values) => values.find((value) => String(value || "").trim()) || "";
const isUuid = (value) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ""));

const normalizeSupplementaryInsurances = (insurances = []) =>
  (Array.isArray(insurances) ? insurances : [])
    .map((insurance, index) => ({
      id: insurance.id || `supplementary-insurance-${index + 1}`,
      provider: insurance.provider || insurance.name || "",
      policyNumber: insurance.policyNumber || insurance.memberPolicyNumber || insurance.memberNumber || "",
      groupNumber: insurance.groupNumber || "",
      notes: insurance.notes || "",
    }));

const supplementaryInsurancesFromBio = (bio = {}) => {
  if (!bio || typeof bio !== "object") return [];
  const source = bio.sourceForm && typeof bio.sourceForm === "object" ? bio.sourceForm : {};
  const coverage = bio.healthAndCoverage && typeof bio.healthAndCoverage === "object" ? bio.healthAndCoverage : {};
  const candidates = [
    source.otherInsurances,
    bio.otherInsurances,
    bio.supplementaryInsurances,
    coverage.supplementaryInsurances,
  ];
  const insurances = candidates.find((value) => Array.isArray(value) && value.length) || [];
  return normalizeSupplementaryInsurances(insurances);
};

const onboardingBioFormFromInitialAssessment = (bio = {}) => {
  if (!bio || typeof bio !== "object") return {};
  const source = bio.sourceForm && typeof bio.sourceForm === "object" ? bio.sourceForm : {};
  const personal = bio.personalInformation && typeof bio.personalInformation === "object" ? bio.personalInformation : {};
  const address = bio.address && typeof bio.address === "object" ? bio.address : {};
  const location = bio.locationInformation && typeof bio.locationInformation === "object" ? bio.locationInformation : {};
  const contactInfo = bio.contactInformation && typeof bio.contactInformation === "object" ? bio.contactInformation : {};
  const residentContactInfo = bio.residentContactInformation && typeof bio.residentContactInformation === "object" ? bio.residentContactInformation : {};
  const phones = bio.phones && typeof bio.phones === "object" ? bio.phones : {};
  const contactPhones = contactInfo.phones && typeof contactInfo.phones === "object" ? contactInfo.phones : {};
  const residentContactPhones = residentContactInfo.phones && typeof residentContactInfo.phones === "object" ? residentContactInfo.phones : {};
  const coverage = bio.healthAndCoverage && typeof bio.healthAndCoverage === "object" ? bio.healthAndCoverage : {};
  const form = { ...source };
  form.firstName = bio.firstName || personal.firstName || source.firstName || "";
  form.middleName = bio.middleName || personal.middleName || source.middleName || "";
  form.lastName = bio.lastName || personal.lastName || source.lastName || "";
  form.preferredName = bio.preferredName || personal.preferredName || source.preferredName || "";
  form.dateOfBirth = dateInputValueFrom(bio.dateOfBirth || bio.birthDate || bio.dob || personal.birthDate || personal.dateOfBirth || personal.dob || source.dateOfBirth);
  form.socialSecurityNumber = bio.socialSecurityNumber || personal.socialSecurityNumber || source.socialSecurityNumber || "";
  form.sex = displaySexValue(bio.sex || bio.gender || personal.sex || personal.gender || source.sex);
  form.primaryLanguage = bio.primaryLanguage || bio.language || bio.preferredLanguage || personal.primaryLanguage || personal.language || personal.preferredLanguage || source.primaryLanguage || "";
  form.heightFeet = bio.heightFeet ?? personal.heightFeet ?? source.heightFeet ?? "";
  form.heightInches = bio.heightInches ?? personal.heightInches ?? source.heightInches ?? "";
  form.weightLbs = bio.weightLbs ?? personal.weightLbs ?? source.weightLbs ?? "";
  form.race = bio.race || personal.race || source.race || "";
  form.placeOfBirth = bio.placeOfBirth || personal.placeOfBirth || source.placeOfBirth || "";
  form.religion = bio.religion || personal.religion || source.religion || "";
  form.codeStatus = bio.codeStatus || coverage.codeStatus || source.codeStatus || "";
  form.advancedDirectives = firstBioValue(
    source.advancedDirectives,
    yesNoFromBoolean(bio.advancedDirectives),
    yesNoFromBoolean(coverage.advancedDirectives),
  );
  form.medicareNumber = bio.medicareNumber || coverage.medicareNumber || source.medicareNumber || "";
  form.medicaidNumber = bio.medicaidNumber || coverage.medicaidNumber || source.medicaidNumber || "";
  form.hasOtherInsurance = supplementaryInsurancesFromBio(bio).length ? "Yes" : source.hasOtherInsurance || "";
  form.mobilePhone = firstBioValue(
    bio.mobilePhone,
    bio.mobile,
    bio.phone,
    bio.phoneNumber,
    phones.mobile,
    phones.cell,
    phones.cellPhone,
    phones.primary,
    contactPhones.mobile,
    contactPhones.cell,
    contactPhones.cellPhone,
    contactPhones.primary,
    residentContactPhones.mobile,
    residentContactPhones.cell,
    residentContactPhones.cellPhone,
    residentContactPhones.primary,
    contactInfo.mobilePhone,
    contactInfo.mobile,
    contactInfo.phone,
    contactInfo.phoneNumber,
    contactInfo.primaryPhone,
    residentContactInfo.mobilePhone,
    residentContactInfo.mobile,
    residentContactInfo.phone,
    residentContactInfo.phoneNumber,
    residentContactInfo.primaryPhone,
    source.mobilePhone,
    source.mobile,
    source.phone,
    source.phoneNumber,
  );
  form.officePhone = firstBioValue(
    bio.officePhone,
    bio.workPhone,
    bio.businessPhone,
    phones.office,
    phones.work,
    phones.business,
    contactPhones.office,
    contactPhones.work,
    contactPhones.business,
    residentContactPhones.office,
    residentContactPhones.work,
    residentContactPhones.business,
    contactInfo.officePhone,
    contactInfo.workPhone,
    contactInfo.businessPhone,
    residentContactInfo.officePhone,
    residentContactInfo.workPhone,
    residentContactInfo.businessPhone,
    source.officePhone,
    source.workPhone,
  );
  form.homePhone = firstBioValue(
    bio.homePhone,
    bio.secondaryPhone,
    phones.home,
    phones.secondary,
    contactPhones.home,
    contactPhones.secondary,
    residentContactPhones.home,
    residentContactPhones.secondary,
    contactInfo.homePhone,
    contactInfo.secondaryPhone,
    residentContactInfo.homePhone,
    residentContactInfo.secondaryPhone,
    source.homePhone,
    source.secondaryPhone,
  );
  form.preferredPhone = preferredPhoneFieldFor(
    firstBioValue(bio.preferredPhone, phones.preferred, contactPhones.preferred, residentContactPhones.preferred, source.preferredPhone),
    form,
  ) || (firstBioValue(contactInfo.primaryPhone, residentContactInfo.primaryPhone, phones.primary, contactPhones.primary, residentContactPhones.primary) ? "mobilePhone" : "");
  form.email = bio.email || bio.primaryEmail || contactInfo.primaryEmail || contactInfo.email || residentContactInfo.primaryEmail || residentContactInfo.email || source.email || "";
  form.addressSearch = bio.addressSearch || bio.addressText || bio.formattedAddress || address.formattedAddress || location.formattedAddress || source.addressSearch || source.formattedAddress || "";
  form.address = bio.addressText || bio.address || bio.addressLine1 || address.address || address.addressLine1 || address.formattedAddress || location.address || location.addressLine1 || location.formattedAddress || source.address || source.addressLine1 || "";
  form.addressLine2 = bio.addressLine2 || address.addressLine2 || location.addressLine2 || source.addressLine2 || "";
  form.city = bio.city || address.city || location.city || source.city || "";
  form.county = bio.county || address.county || location.county || source.county || "";
  form.state = bio.state || address.state || location.state || source.state || "";
  form.country = bio.country || address.country || location.country || source.country || "";
  form.zip = bio.zip || bio.zipCode || address.zipCode || address.zip || location.zipCode || location.zip || source.zip || source.zipCode || "";
  form.latitude = bio.latitude || address.latitude || location.latitude || source.latitude || "";
  form.longitude = bio.longitude || address.longitude || location.longitude || source.longitude || "";
  form.placeId = bio.placeId || address.placeId || location.placeId || source.placeId || "";
  form.addressType = bio.addressType || address.addressType || location.addressType || source.addressType || "";
  form.addressTypeOther = bio.addressTypeOther || bio.otherAddressType || address.addressTypeOther || address.otherAddressType || location.addressTypeOther || location.otherAddressType || source.addressTypeOther || source.otherAddressType || "";
  form.bioNotes = bio.backgroundNotes || bio.notes || source.backgroundNotes || source.bioNotes || "";
  return form;
};

const prefixedAddressSummary = (formData, prefix) => {
  const addressType =
    formData[contactField(prefix, "addressType")] === "Other"
      ? formData[contactField(prefix, "addressTypeOther")]
      : formData[contactField(prefix, "addressType")];
  const addressParts = ["address", "city", "county", "state", "country", "zip"]
    .map((field) => formData[contactField(prefix, field)])
    .filter(Boolean)
    .join(", ");
  return [addressType ? `Type: ${addressType}` : "", addressParts].filter(Boolean).join(" | ");
};

const documentStorageFileId = (document) => document?.fileId || document?.storageFileId || "";
const usablePersistedDocumentUrl = (url) => (url && !String(url).startsWith("blob:") ? url : "");
const documentHasStorageFile = (document) => Boolean(documentStorageFileId(document));
const documentNeedsStorageUpload = (document) => Boolean(document?.needsUpload && document?.file && !documentHasStorageFile(document));
const documentPreviewUrl = (document, signedUrls = {}) => {
  const signedUrl = signedUrls[documentStorageFileId(document)] || "";
  return (
    document?.localPreviewUrl ||
    signedUrl ||
    usablePersistedDocumentUrl(document?.signedUrl) ||
    usablePersistedDocumentUrl(document?.signedURL) ||
    usablePersistedDocumentUrl(document?.previewUrl) ||
    usablePersistedDocumentUrl(document?.thumbnailUrl) ||
    usablePersistedDocumentUrl(document?.url) ||
    usablePersistedDocumentUrl(document?.fileUrl) ||
    ""
  );
};
const documentDownloadUrl = (document, signedUrls = {}) => {
  const signedUrl = signedUrls[documentStorageFileId(document)] || "";
  return (
    document?.localPreviewUrl ||
    signedUrl ||
    usablePersistedDocumentUrl(document?.signedUrl) ||
    usablePersistedDocumentUrl(document?.signedURL) ||
    usablePersistedDocumentUrl(document?.downloadUrl) ||
    usablePersistedDocumentUrl(document?.downloadURL) ||
    usablePersistedDocumentUrl(document?.url) ||
    usablePersistedDocumentUrl(document?.previewUrl) ||
    ""
  );
};
const documentDisplayName = (document = {}, fallback = "Document") =>
  document.pageTitle || document.label || document.name || document.fileName || document.originalFileName || document.caption || fallback;
const documentPreviewName = (document = {}, fallback = "Document preview") =>
  document.name || document.fileName || document.pageTitle || document.label || fallback;
const documentSourceName = (document = {}, fallback = "Uploaded assessment page") =>
  document.originalFileName || document.sourceFileName || document.fileName || document.name || fallback;
const documentIdentity = (document = {}) =>
  String(document.fileId || document.storageFileId || document.id || document.fileName || document.name || "");
const isInitialAssessmentPageDocument = (document = {}) => {
  const text = [
    document.pageTitle,
    document.caption,
    document.description,
    document.name,
    document.label,
  ].filter(Boolean).join(" ").toLowerCase();
  return Boolean(document.pageNumber && text.includes("assessment page")) ||
    text.includes("initial state assessment") ||
    text.includes("assessment page");
};
const withoutAssessmentPageDocuments = (documents = [], sourcePages = []) => {
  const sourceIds = new Set(sourcePages.map(documentIdentity).filter(Boolean));
  return (Array.isArray(documents) ? documents : []).filter((document) => {
    const id = documentIdentity(document);
    return !(isInitialAssessmentPageDocument(document) || (id && sourceIds.has(id)));
  });
};
const persistedDocumentUrl = (url) => (url && !String(url).startsWith("blob:") ? url : "");
const uploadedDocumentsFromPayload = (payload = {}) => {
  const stages = payload?.onboardingStages || {};
  return (
    stages.documents?.documents?.uploadedDocuments ||
    payload?.documents?.documents?.uploadedDocuments ||
    payload?.documents?.uploadedDocuments ||
    []
  );
};
const mergeReturnedDocumentFiles = (currentDocuments, response) => {
  const returnedDocuments = uploadedDocumentsFromPayload(response?.payload || response?.previewPayload || {});
  if (!Array.isArray(returnedDocuments) || !returnedDocuments.length) {
    return currentDocuments;
  }
  const keyFor = (document) => String(document?.fileId || document?.storageFileId || document?.fileName || document?.name || "");
  const returnedByKey = new Map(returnedDocuments.map((document) => [keyFor(document), document]).filter(([key]) => key));
  return currentDocuments.map((document) => {
    const returnedDocument = returnedByKey.get(keyFor(document));
    if (!returnedDocument) {
      return document;
    }
    const signedUrl = returnedDocument.signedUrl || returnedDocument.previewUrl || returnedDocument.downloadUrl || document.signedUrl || "";
    return {
      ...document,
      ...returnedDocument,
      id: document.id,
      file: document.file,
      localPreviewUrl: document.localPreviewUrl,
      fileId: returnedDocument.fileId || returnedDocument.storageFileId || document.fileId || document.storageFileId || null,
      storageFileId: returnedDocument.storageFileId || returnedDocument.fileId || document.storageFileId || document.fileId || null,
      signedUrl,
      previewUrl: signedUrl || returnedDocument.previewUrl || document.previewUrl || "",
      downloadUrl: returnedDocument.downloadUrl || signedUrl || document.downloadUrl || "",
      needsUpload: false,
      uploaded: returnedDocument.uploaded ?? true,
    };
  });
};
const syncResidentCollateralContacts = async (residentId, contactPayloads = []) => {
  const existingContacts = await careService.residentContacts.listForResident(residentId).catch(() => []);
  const existingIds = new Set(existingContacts.map((contact) => contact.id).filter(Boolean));
  const submittedIds = new Set(contactPayloads.map((contact) => contact.id).filter(isUuid));

  await Promise.all(
    existingContacts
      .filter((contact) => contact.id && !submittedIds.has(contact.id))
      .map((contact) => careService.residentContacts.deleteForResident(residentId, contact.id)),
  );

  await Promise.all(contactPayloads.map((contact) => {
    const { id, ...contactPayload } = contact;
    if (isUuid(contact.id) && existingIds.has(contact.id)) {
      return careService.residentContacts.updateForResident(residentId, contact.id, contactPayload);
    }
    return careService.residentContacts.createForResident(residentId, contactPayload);
  }));

  return careService.residentContacts.listForResident(residentId);
};

const isImageDocument = (document) => {
  const type = String(document?.type || document?.mimeType || document?.contentType || document?.file?.type || "").toLowerCase();
  const name = String(document?.fileName || document?.name || document?.file?.name || "").toLowerCase();
  const url = String(documentPreviewUrl(document) || "").toLowerCase();
  return (
    Boolean(document?.isPhoto || document?.isImage) ||
    type.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(\?|$)/i.test(name) ||
    /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(\?|$)/i.test(url)
  );
};

const isBrowserPreviewableImage = (document) => {
  const type = String(document?.type || document?.mimeType || document?.contentType || document?.file?.type || "").toLowerCase();
  const url = String(documentPreviewUrl(document) || "").toLowerCase();
  const name = String(document?.fileName || document?.name || document?.file?.name || "").toLowerCase();
  if (type.includes("heic") || type.includes("heif")) return false;
  if (/\.(heic|heif)(\?|$)/i.test(url) || /\.(heic|heif)$/i.test(name)) return false;
  return ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/bmp", "image/svg+xml"].includes(type) ||
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(url) ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(name);
};

const buildResidentRegistrationPayload = ({
  assessmentExtraction,
  assessmentFile,
  assessmentFileMeta,
  assessmentImported,
  contacts,
  customCareQuestions,
  documentFiles,
  formData,
  onboardingResidentId,
  onboardingStepStatus,
  otherInsurances,
  selectedWorkspace,
  selectedWorkspaceId,
  signedDocumentValidations,
}) => {
  const residentContacts = contacts.map((contact) => {
    const rawName = formData[contactField(contact.prefix, "name")] || "";
    const name = splitName(rawName);
    const relationship = contact.type === "Other" ? contact.relationshipOther || "Other" : contact.type || "";
    const contactType = contactTypeFor(relationship);
    const isDecisionMaker = ["POWER_OF_ATTORNEY", "GUARDIAN"].includes(contactType);
    const address = addressPayload(formData, contact.prefix);
    return {
      id: isUuid(contact.id) ? contact.id : null,
      residentId: onboardingResidentId || null,
      contactType,
      relationship,
      ...name,
      name: rawName,
      fullName: rawName,
      organizationName: formData[contactField(contact.prefix, "organizationName")] || "",
      email: formData[contactField(contact.prefix, "email")] || "",
      fax: formData[contactField(contact.prefix, "fax")] || "",
      phones: phonePayload(formData, contact.prefix),
      address,
      locationInformation: address,
      primaryContact: Boolean(!contacts.indexOf(contact)),
      emergencyContact: contactType === "EMERGENCY_CONTACT" || isDecisionMaker,
      decisionMaker: isDecisionMaker,
      nextOfKin: isDecisionMaker || Boolean(!contacts.indexOf(contact)),
      notes: contact.notes || "",
    };
  });

  return {
    resident: {
      workspaceId: selectedWorkspaceId || null,
      organizationId: selectedWorkspace?.organizationId || null,
      businessEntityId: null,
      businessLocationId: null,
      process: {
        patientType: "NEW",
        startDate: formData.processStartDate || formData.admissionDate || new Date().toISOString().slice(0, 10),
        endDate: formData.processEndDate || null,
        status: "PROSPECTIVE",
        existingResidentId: null,
      },
      personalInformation: {
        firstName: formData.firstName || "",
        middleName: formData.middleName || "",
        lastName: formData.lastName || "",
        preferredName: formData.preferredName || "",
        birthDate: formData.dateOfBirth || null,
        sex: apiSexValue(formData.sex),
        socialSecurityNumber: formData.socialSecurityNumber || "",
        primaryLanguage: formData.primaryLanguage || "",
        heightFeet: formData.heightFeet === "" || formData.heightFeet == null ? null : Number(formData.heightFeet),
        heightInches: formData.heightInches === "" || formData.heightInches == null ? null : Number(formData.heightInches),
        weightLbs: formData.weightLbs === "" || formData.weightLbs == null ? null : Number(formData.weightLbs),
        race: formData.race || "",
        raceOther: formData.race === "Other" ? formData.raceOther || "" : "",
        maritalStatus: formData.maritalStatus || "",
        placeOfBirth: formData.placeOfBirth || "",
        religion: formData.religion || "",
      },
      healthAndCoverage: {
        codeStatus: formData.codeStatus || "",
        advancedDirectives: yesNoBoolean(formData.advancedDirectives),
        medicareNumber: formData.medicareNumber || "",
        medicaidNumber: formData.medicaidNumber || "",
        supplementaryInsurances: (formData.hasOtherInsurance === "Yes" || otherInsurances.length) ? otherInsurances.map((insurance) => ({
          provider: insurance.provider || "",
          memberPolicyNumber: insurance.policyNumber || "",
          groupNumber: insurance.groupNumber || "",
          notes: insurance.notes || "",
        })) : [],
      },
      backgroundNotes: formData.bioNotes || "",
      notes: formData.bioNotes || "",
      onboardingStepStatus,
      contactInformation: {
        primaryEmail: formData.email || "",
        primaryPhone: phoneValueFor(formData),
        secondaryPhone: [formData.mobilePhone, formData.officePhone, formData.homePhone].find((phone) => phone && phone !== phoneValueFor(formData)) || "",
        mobilePhone: formData.mobilePhone || "",
        officePhone: formData.officePhone || "",
        homePhone: formData.homePhone || "",
        preferredPhone: formData.preferredPhone ? formData[formData.preferredPhone] || "" : "",
        email: formData.email || "",
        phones: phonePayload(formData),
      },
      residentContactInformation: {
        primaryEmail: formData.email || "",
        primaryPhone: phoneValueFor(formData),
        secondaryPhone: [formData.mobilePhone, formData.officePhone, formData.homePhone].find((phone) => phone && phone !== phoneValueFor(formData)) || "",
        mobilePhone: formData.mobilePhone || "",
        officePhone: formData.officePhone || "",
        homePhone: formData.homePhone || "",
        preferredPhone: formData.preferredPhone ? formData[formData.preferredPhone] || "" : "",
        email: formData.email || "",
        phones: phonePayload(formData),
      },
      address: addressPayload(formData),
      admission: {
        admissionDate: formData.admissionDate || null,
        dischargeDate: formData.dischargeDate || null,
        conditionDiagnosisAndCauseForAdmission: formData.admissionDescription || "",
      },
      contacts: residentContacts,
      careHistory: {
        admittedAtHospital: yesNoBoolean(formData.wasAdmittedAtHospital),
        hospitalAdmission: formData.wasAdmittedAtHospital === "Yes" ? {
          name: formData.hospitalName || "",
          contactName: formData.hospitalContactName || "",
          email: formData.hospitalEmail || "",
          phones: {
            mobile: formData.hospitalPhone || "",
            office: "",
            home: "",
            preferred: formData.hospitalPhone || "",
          },
          address: addressPayload(formData, "hospital", { includeAddressType: false }),
          notes: formData.admissionDescription || "",
        } : null,
        comingFromAnotherFamilyHome: yesNoBoolean(formData.fromAnotherFamilyHome),
        previousFamilyHome: formData.fromAnotherFamilyHome === "Yes" ? {
          name: formData.priorFamilyHomeName || "",
          contactName: formData.priorFamilyHomeContactName || "",
          email: formData.priorFamilyHomeEmail || "",
          phones: {
            mobile: formData.priorFamilyHomePhone || "",
            office: "",
            home: "",
            preferred: formData.priorFamilyHomePhone || "",
          },
          address: addressPayload(formData, "priorFamilyHome", { includeAddressType: false }),
          years: formData.priorFamilyHomeYears ? Number(formData.priorFamilyHomeYears) : null,
          months: formData.priorFamilyHomeMonths ? Number(formData.priorFamilyHomeMonths) : null,
          notes: formData.priorFamilyHomeDetails || "",
        } : null,
        hadPriorCaregiver: yesNoBoolean(formData.hadPriorCaregiver),
        priorCaregiver: formData.hadPriorCaregiver === "Yes"
          ? personPayload({
              formData,
              name: formData.priorCaregiverName || "",
              prefix: "priorCaregiver",
              relationship: formData.priorCaregiverRelationship || "",
              email: formData.priorCaregiverEmail || "",
              notes: formData.priorCaregiverDetails || "",
            })
          : null,
        notes: [formData.admissionDescription, formData.priorFamilyHomeDetails, formData.priorCaregiverDetails].filter(Boolean).join("\n\n"),
      },
      initialCareSnapshot: {
        conditionSummary: formData.admissionDescription || "",
        diagnosis: formData.diagnosis || "",
        allergies: formData.allergies || "",
        diet: formData.diet || "",
        medicationNotes: formData.medicationNotes || "",
        careNeeded: formData.careNeeded || "",
        behaviorSafetyNotes: formData.behaviorSafetyNotes || "",
        customQuestions: customCareQuestions.map((question) => ({
          question: question.question || "",
          answer: question.response || "",
        })),
        preliminaryPlan: {
          goals: formData.preliminaryPlanGoals || "",
          careApproach: formData.preliminaryPlanCareApproach || "",
          staffInstructions: formData.preliminaryPlanStaffInstructions || "",
          familyPreferences: formData.preliminaryPlanFamilyPreferences || "",
          risksAndFollowUp: formData.preliminaryPlanRisksAndFollowUp || "",
        },
      },
      documents: {
        uploadedDocuments: documentFiles.map((document) => ({
          fileId: document.fileId || document.storageFileId || null,
          name: document.name || document.fileName || "",
          fileName: document.fileName || document.name || "",
          description: document.description || "",
          type: document.type || "",
          size: document.size || null,
          signedUrl: persistedDocumentUrl(document.signedUrl),
          previewUrl: persistedDocumentUrl(document.previewUrl),
          downloadUrl: persistedDocumentUrl(document.downloadUrl || document.signedUrl),
          uploaded: Boolean(document.uploaded),
          confirmed: Boolean(document.confirmed),
          uploadedAt: document.uploadedAt || null,
          storageStatus: document.storageStatus || (documentHasStorageFile(document) ? "CONFIRMED" : "PENDING_UPLOAD"),
        })),
        signedDocumentValidations: signedDocumentValidations.map((document) => ({
          documentName: document.documentName || "",
          signedVerified: Boolean(document.signedVerified),
          fileUploaded: Boolean(document.fileUploaded),
          notes: document.notes || "",
        })),
      },
      sourceForm: {
        assessmentExtraction,
        formData,
        contacts,
        otherInsurances,
        customCareQuestions,
        documentFiles,
        onboardingStepStatus,
        signedDocumentValidations,
        assessmentFile: assessmentFile
          ? {
              name: assessmentFile.name,
              size: assessmentFile.size,
              type: assessmentFile.type,
              imported: assessmentImported,
            }
          : assessmentFileMeta
            ? { ...assessmentFileMeta, imported: assessmentImported }
            : null,
      },
    },
  };
};

const buildOnboardingStagePayloads = (options) => {
  const fullPayload = buildResidentRegistrationPayload(options);
  const resident = fullPayload.resident;
  const base = {
    workspaceId: resident.workspaceId,
    organizationId: resident.organizationId,
    onboardingStepStatus: resident.onboardingStepStatus,
  };
  return {
    bio: {
      resident: {
        workspaceId: resident.workspaceId,
        organizationId: resident.organizationId,
        businessEntityId: resident.businessEntityId,
        businessLocationId: resident.businessLocationId,
        process: resident.process,
        personalInformation: resident.personalInformation,
        healthAndCoverage: resident.healthAndCoverage,
        backgroundNotes: resident.backgroundNotes,
        notes: resident.notes,
        onboardingStepStatus: resident.onboardingStepStatus,
        address: resident.address,
        admission: resident.admission,
        sourceForm: {
          assessmentExtraction: resident.sourceForm.assessmentExtraction,
          formData: resident.sourceForm.formData,
          otherInsurances: resident.sourceForm.otherInsurances,
          assessmentFile: resident.sourceForm.assessmentFile,
          onboardingStepStatus: resident.onboardingStepStatus,
        },
      },
    },
    contacts: {
      ...base,
      payload: {
        residentId: options.onboardingResidentId || null,
        contacts: resident.contacts,
      },
    },
    careHistory: {
      ...base,
      payload: {
        residentId: options.onboardingResidentId || null,
        careHistory: resident.careHistory,
      },
    },
    care: {
      ...base,
      payload: {
        residentId: options.onboardingResidentId || null,
        initialCareSnapshot: resident.initialCareSnapshot,
      },
    },
    preliminaryPlan: {
      ...base,
      payload: {
        residentId: options.onboardingResidentId || null,
        initialCareSnapshot: resident.initialCareSnapshot,
      },
    },
    documents: {
      ...base,
      payload: {
        residentId: options.onboardingResidentId || null,
        documents: resident.documents,
      },
    },
  };
};

const errorMessageFrom = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  "Could not submit resident. Please review the highlighted information and try again.";

const emptyDraft = {
  currentStep: 0,
  contacts: [],
  otherInsurances: [],
  customCareQuestions: [],
  documentFiles: [],
  onboardingStepStatus: onboardingStepStatusFor(0),
  onboardingResidentId: "",
  onboardingRegistrationId: "",
  assessmentProcessId: "",
  signedDocumentValidations: signedDocumentDefaults.map((documentName) => ({
    id: `signed-${documentName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    documentName,
    signedVerified: false,
    fileUploaded: false,
    notes: "",
  })),
  selectedContactId: "",
  assessmentFileMeta: null,
  assessmentImported: false,
  pendingAssessmentImport: null,
  formData: {},
};

const formatSocialSecurityNumber = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const normalizeStateSelection = (value) => {
  if (!value) return "";
  if (usStateOptions.includes(value)) return value;
  return usStateAbbreviations[value.toUpperCase()] || "";
};

function ResidentAddressFields({ appConfigLoading, compact = false, formData, googleMapsApiKey, prefix = "", showAddressType = true, title = "Address", updateField }) {
  const fieldName = (field) => addressFieldName(prefix, field);
  const applyAddressFields = (addressFields) => {
    updateField(fieldName("address"), addressFields.addressLine1 || addressFields.formattedAddress || formData[fieldName("address")] || "");
    updateField(fieldName("city"), addressFields.city || "");
    updateField(fieldName("county"), addressFields.county || "");
    updateField(fieldName("state"), normalizeStateSelection(addressFields.state));
    updateField(fieldName("country"), addressFields.country || "");
    updateField(fieldName("zip"), addressFields.zipCode || "");
  };
  const searchField = fieldName("addressSearch");
  const addressField = fieldName("address");
  const addressTypeField = fieldName("addressType");
  const addressTypeOtherField = fieldName("addressTypeOther");
  const selectedAddressType = formData[addressTypeField] || "";

  return (
    <div className={`resident-address-section full-span${compact ? " is-compact" : ""}`}>
      <div className="resident-form-section-title">
        <i className="ti ti-map-pin" />
        <span>{title}</span>
      </div>
      <label className="resident-form-field full-span resident-address-search">
        <span>Location search</span>
        {appConfigLoading && !googleMapsApiKey ? (
          <input type="text" disabled placeholder="Loading map address search..." />
        ) : (
          <GooglePlacesAutocompleteInput
            googleMapsApiKey={googleMapsApiKey}
            placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
            types={["address"]}
            value={formData[searchField] || formData[addressField] || ""}
            onChange={(value) => {
              updateField(searchField, value);
              if (!googleMapsApiKey) updateField(addressField, value);
            }}
            onResolvePlace={async ({ details, prediction }) => {
              const resolvedAddress = details?.formatted_address || prediction?.description || formData[searchField] || "";
              updateField(searchField, resolvedAddress);
              updateField(addressField, resolvedAddress);
              if (details) {
                applyAddressFields({
                  ...addressFieldsFromPlaceDetails(details),
                  formattedAddress: resolvedAddress,
                });
              }
            }}
          />
        )}
      </label>
      <div className="resident-address-grid">
        {showAddressType ? (
          <label className="resident-form-field">
            <span>Address type</span>
            <select
              name={addressTypeField}
              value={selectedAddressType}
              onChange={(event) => updateField(addressTypeField, event.target.value)}
            >
              <option value="">Select address type</option>
              {addressTypeOptions.map((option) => (
                <option value={option} key={option}>{option}</option>
              ))}
            </select>
          </label>
        ) : null}
        {showAddressType && selectedAddressType === "Other" ? (
          <label className="resident-form-field">
            <span>Specify address type</span>
            <input
              name={addressTypeOtherField}
              type="text"
              value={formData[addressTypeOtherField] || ""}
              onChange={(event) => updateField(addressTypeOtherField, event.target.value)}
            />
          </label>
        ) : null}
        {addressFields.map(([field, label]) => (
          <label className={`resident-form-field resident-address-field resident-address-field-${field}`} key={field}>
            <span>{label}</span>
            {field === "state" ? (
              <select
                name={fieldName(field)}
                value={formData[fieldName(field)] || ""}
                onChange={(event) => updateField(fieldName(field), event.target.value)}
              >
                <option value="">Select state</option>
                {usStateOptions.map((option) => (
                  <option value={option} key={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input
                name={fieldName(field)}
                type="text"
                value={formData[fieldName(field)] || ""}
                onChange={(event) => updateField(fieldName(field), event.target.value)}
              />
            )}
          </label>
        ))}
      </div>
    </div>
  );
}

const contactSummary = (formData, key) => {
  const addressType =
    formData[contactField(key, "addressType")] === "Other"
      ? formData[contactField(key, "addressTypeOther")]
      : formData[contactField(key, "addressType")];
  const addressParts = ["address", "city", "county", "state", "country", "zip"]
    .map((field) => formData[contactField(key, field)])
    .filter(Boolean)
    .join(", ");
  const values = [
    formData[contactField(key, "name")],
    formData[contactField(key, "organizationName")] ? `Organization: ${formData[contactField(key, "organizationName")]}` : "",
    formData[contactField(key, "mobilePhone")] ? `Mobile: ${formData[contactField(key, "mobilePhone")]}` : "",
    formData[contactField(key, "officePhone")] ? `Office: ${formData[contactField(key, "officePhone")]}` : "",
    formData[contactField(key, "homePhone")] ? `Home: ${formData[contactField(key, "homePhone")]}` : "",
    formData[contactField(key, "fax")] ? `Fax: ${formData[contactField(key, "fax")]}` : "",
    formData[contactField(key, "preferredPhone")] ? `Preferred: ${preferredPhoneLabel(formData[contactField(key, "preferredPhone")])}` : "",
    formData[contactField(key, "fax")] ? `Fax: ${formData[contactField(key, "fax")]}` : "",
    formData[contactField(key, "email")] ? `Email: ${formData[contactField(key, "email")]}` : "",
    addressType ? `Address type: ${addressType}` : "",
    addressParts ? `Address: ${addressParts}` : "",
  ].filter(Boolean);
  return values.join(" | ");
};

function FacesheetContactCard({ appConfigLoading, canRemove = true, contact, formData, googleMapsApiKey, onRemove, updateContact, updateField }) {
  const prefix = contact.prefix;
  const fieldName = (field) => contactField(prefix, field);
  const updateContactAddressFromPlace = (details, resolvedAddress) => {
    const fields = addressFieldsFromPlaceDetails(details);
    updateField(fieldName("address"), fields.addressLine1 || resolvedAddress);
    updateField(fieldName("city"), fields.city || "");
    updateField(fieldName("county"), fields.county || "");
    updateField(fieldName("state"), fields.state || "");
    updateField(fieldName("country"), fields.country || "");
    updateField(fieldName("zip"), fields.zipCode || "");
    updateField(fieldName("latitude"), fields.latitude || "");
    updateField(fieldName("longitude"), fields.longitude || "");
    updateField(fieldName("placeId"), fields.placeId || "");
  };
  return (
    <article className="state-assessment-contact-card">
      <div className="state-assessment-contact-card-title">
        <strong>{contact.type || "Contact"}</strong>
        <button aria-label="Remove contact" disabled={!canRemove} type="button" onClick={() => onRemove(contact.id)}>
          <i className="ti ti-trash" />
        </button>
      </div>
      <div className="resident-form-grid compact">
        <label className="resident-form-field">
          <span>Relationship</span>
          <select value={contact.type || ""} onChange={(event) => updateContact(contact.id, "type", event.target.value)}>
            <option value="">Select relationship</option>
            {contactRelationshipOptions.map((option) => (
              <option value={option} key={option}>{option}</option>
            ))}
          </select>
        </label>
        {contact.type === "Other" ? (
          <label className="resident-form-field">
            <span>Specify relationship</span>
            <input value={contact.relationshipOther || ""} onChange={(event) => updateContact(contact.id, "relationshipOther", event.target.value)} />
          </label>
        ) : null}
        <label className="resident-form-field">
          <span>Name</span>
          <input value={formData[fieldName("name")] || ""} onChange={(event) => updateField(fieldName("name"), event.target.value)} />
        </label>
        <label className="resident-form-field">
          <span>Organization</span>
          <input value={formData[fieldName("organizationName")] || ""} onChange={(event) => updateField(fieldName("organizationName"), event.target.value)} />
        </label>
        {phoneFields.map(([field, label]) => (
          <label className="resident-form-field" key={field}>
            <span>{label}</span>
            <input type="tel" value={formData[fieldName(field)] || ""} onChange={(event) => updateField(fieldName(field), event.target.value)} />
          </label>
        ))}
        <label className="resident-form-field">
          <span>Preferred phone</span>
          <select value={formData[fieldName("preferredPhone")] || ""} onChange={(event) => updateField(fieldName("preferredPhone"), event.target.value)}>
            <option value="">Select preferred</option>
            {preferredPhoneOptions.map(([value, label]) => (
              <option value={value} key={value}>{label}</option>
            ))}
          </select>
        </label>
        <label className="resident-form-field">
          <span>Email</span>
          <input type="email" value={formData[fieldName("email")] || ""} onChange={(event) => updateField(fieldName("email"), event.target.value)} />
        </label>
        <label className="resident-form-field full-span resident-address-search">
          <span>Location search</span>
          {appConfigLoading && !googleMapsApiKey ? (
            <input type="text" disabled placeholder="Loading map address search..." />
          ) : (
            <GooglePlacesAutocompleteInput
              googleMapsApiKey={googleMapsApiKey}
              placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
              types={["address"]}
              value={formData[fieldName("addressSearch")] || formData[fieldName("address")] || ""}
              onChange={(value) => {
                updateField(fieldName("addressSearch"), value);
                if (!googleMapsApiKey) updateField(fieldName("address"), value);
              }}
              onResolvePlace={async ({ details, prediction }) => {
                const resolvedAddress = details?.formatted_address || prediction?.description || formData[fieldName("addressSearch")] || "";
                updateField(fieldName("addressSearch"), resolvedAddress);
                updateField(fieldName("address"), resolvedAddress);
                if (details) updateContactAddressFromPlace(details, resolvedAddress);
              }}
            />
          )}
        </label>
        <label className="resident-form-field">
          <span>Address type</span>
          <select value={formData[fieldName("addressType")] || ""} onChange={(event) => updateField(fieldName("addressType"), event.target.value)}>
            <option value="">Select address type</option>
            {addressTypeOptions.map((option) => (
              <option value={option} key={option}>{option}</option>
            ))}
          </select>
        </label>
        {formData[fieldName("addressType")] === "Other" ? (
          <label className="resident-form-field">
            <span>Specify address type</span>
            <input value={formData[fieldName("addressTypeOther")] || ""} onChange={(event) => updateField(fieldName("addressTypeOther"), event.target.value)} />
          </label>
        ) : null}
        <label className="resident-form-field">
          <span>Address line 1</span>
          <input value={formData[fieldName("address")] || ""} onChange={(event) => updateField(fieldName("address"), event.target.value)} />
        </label>
        <label className="resident-form-field">
          <span>Address line 2</span>
          <input value={formData[fieldName("addressLine2")] || ""} onChange={(event) => updateField(fieldName("addressLine2"), event.target.value)} />
        </label>
        <label className="resident-form-field">
          <span>City</span>
          <input value={formData[fieldName("city")] || ""} onChange={(event) => updateField(fieldName("city"), event.target.value)} />
        </label>
        <label className="resident-form-field">
          <span>County</span>
          <input value={formData[fieldName("county")] || ""} onChange={(event) => updateField(fieldName("county"), event.target.value)} />
        </label>
        <label className="resident-form-field">
          <span>State</span>
          <input value={formData[fieldName("state")] || ""} onChange={(event) => updateField(fieldName("state"), event.target.value)} />
        </label>
        <label className="resident-form-field">
          <span>Zip code</span>
          <input value={formData[fieldName("zip")] || ""} onChange={(event) => updateField(fieldName("zip"), event.target.value)} />
        </label>
        <label className="resident-form-field">
          <span>Country</span>
          <input value={formData[fieldName("country")] || ""} onChange={(event) => updateField(fieldName("country"), event.target.value)} />
        </label>
        <label className="resident-form-field full-span">
          <span>Notes</span>
          <textarea rows={2} value={contact.notes || ""} onChange={(event) => updateContact(contact.id, "notes", event.target.value)} />
        </label>
      </div>
    </article>
  );
}

export default function NewResidentPage({
  embedded = false,
  initialAssessmentContacts = [],
  initialAssessmentBioData = null,
  initialAssessmentExtraction = null,
  initialOnboardingPreview = null,
  initialOnboardingPreviewLoaded = false,
  residentId: embeddedResidentId = "",
  sourceAssessmentPages = [],
}) {
  const { residentId: onboardingRouteResidentId } = useParams();
  const onboardingResidentParam = embeddedResidentId || onboardingRouteResidentId;
  const { user } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const { uploadFile, confirmFileUpload, uploading: filesUploading } = useFileUpload();
  const [currentStep, setCurrentStep] = useState(0);
  const [contacts, setContacts] = useState([]);
  const [otherInsurances, setOtherInsurances] = useState([]);
  const [customCareQuestions, setCustomCareQuestions] = useState([]);
  const [documentFiles, setDocumentFiles] = useState([]);
  const documentSignedUrls = {};
  const [onboardingStepStatus, setOnboardingStepStatus] = useState(emptyDraft.onboardingStepStatus);
  const [onboardingResidentId, setOnboardingResidentId] = useState("");
  const [onboardingRegistrationId, setOnboardingRegistrationId] = useState("");
  const [assessmentProcessId, setAssessmentProcessId] = useState("");
  const [signedDocumentValidations, setSignedDocumentValidations] = useState(emptyDraft.signedDocumentValidations);
  const [selectedContactId, setSelectedContactId] = useState("");
  const [showSsn, setShowSsn] = useState(false);
  const [assessmentFile, setAssessmentFile] = useState(null);
  const [assessmentImported, setAssessmentImported] = useState(false);
  const [assessmentImporting, setAssessmentImporting] = useState(false);
  const [assessmentImportMessage, setAssessmentImportMessage] = useState("");
  const [assessmentExtraction, setAssessmentExtraction] = useState(null);
  const [pendingAssessmentImport, setPendingAssessmentImport] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitErrorTitle, setSubmitErrorTitle] = useState("Resident was not submitted");
  const [submitting, setSubmitting] = useState(false);
  const [savingAction, setSavingAction] = useState("");
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState("");
  const [onboardingPreview, setOnboardingPreview] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [filePreviewRotation, setFilePreviewRotation] = useState(0);
  const [cachedAssessmentFileMeta, setCachedAssessmentFileMeta] = useState(null);
  const assessmentFileRef = useRef(null);
  const documentPreviewUrlsRef = useRef(new Set());
  const previousAutoSaveStepRef = useRef(currentStep);
  const initialAssessmentExtractionKeyRef = useRef("");
  const loadedPreviewResidentIdRef = useRef("");
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    "";
  const activeStep = intakeSteps[currentStep][0];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === intakeSteps.length - 1;
  const displayedAssessmentFile = assessmentFile || cachedAssessmentFileMeta;
  const sourceAssessmentPageList = useMemo(
    () => (Array.isArray(sourceAssessmentPages) ? sourceAssessmentPages : []),
    [sourceAssessmentPages],
  );
  const initialAssessmentBioForm = onboardingBioFormFromInitialAssessment(initialAssessmentBioData);
  const initialAssessmentOtherInsurances = supplementaryInsurancesFromBio(initialAssessmentBioData);
  const hasInitialAssessmentBioForm = Object.values(initialAssessmentBioForm).some((value) => String(value || "").trim());
  const initialAssessmentBioKey = JSON.stringify(initialAssessmentBioForm);
  const initialAssessmentOtherInsuranceKey = JSON.stringify(initialAssessmentOtherInsurances);
  const actorId = user?.id || "";
  const furthestReachedStep = Math.max(0, Number(onboardingStepStatus?.currentStepIndex) || 0);
  const reachableStep = Math.max(furthestReachedStep, currentStep);
  const hasSupplementaryInsurance = otherInsurances.length > 0 || formData.hasOtherInsurance === "Yes";
  const progressStepStatusFor = (stepIndex, statusOverride = "") =>
    onboardingStepStatusFor(Math.max(furthestReachedStep, stepIndex), new Date().toISOString(), statusOverride);
  const renderReadOnlyAssessmentPages = (description = "") => sourceAssessmentPageList.length ? (
    <div className="assessment-source-preview">
      <div className="assessment-source-preview-head">
        <i className="ti ti-files" />
        <span>
          <strong>Initial state assessment pages</strong>
          <small>{description || `${sourceAssessmentPageList.length} uploaded page${sourceAssessmentPageList.length === 1 ? "" : "s"} from the first step`}</small>
        </span>
      </div>
      <div className="assessment-source-preview-list">
        {sourceAssessmentPageList.map((page, index) => {
          const previewUrl = documentPreviewUrl(page, documentSignedUrls) || documentDownloadUrl(page, documentSignedUrls);
          const pageKey = page.id || page.fileId || page.storageFileId || `${page.fileName || "assessment-page"}-${index}`;
          const content = (
            <>
              <i className={`ti ${isImageDocument(page) ? "ti-photo" : "ti-file-text"}`} />
              <span>
                <strong>{documentDisplayName(page, `Page ${index + 1}`)}</strong>
                <small>{documentSourceName(page)}{page.size ? ` | ${Math.round(Number(page.size) / 1024)} KB` : ""}</small>
              </span>
              {previewUrl ? <i className="ti ti-eye" /> : null}
            </>
          );
          return previewUrl ? (
            <button className="assessment-source-page-preview" type="button" key={pageKey} onClick={() => openSourceAssessmentGallery(index)}>
              {content}
            </button>
          ) : (
            <div className="assessment-source-page-preview" key={pageKey}>
              {content}
            </div>
          );
        })}
      </div>
    </div>
  ) : null;
  useEffect(() => () => {
    documentPreviewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    documentPreviewUrlsRef.current.clear();
  }, []);
  const saveDraft = useCallback((stepOverride = currentStep, overrides = {}) => {
    const savedAt = new Date();
    const progressIndex = Math.max(
      Number(onboardingStepStatus?.currentStepIndex) || 0,
      Number(overrides.progressStepIndex) || 0,
      stepOverride,
    );
    const nextStepStatus = overrides.onboardingStepStatus || onboardingStepStatusFor(progressIndex, savedAt.toISOString());
    const draftPayload = {
      currentStep: stepOverride,
      contacts,
      otherInsurances,
      customCareQuestions,
      documentFiles: overrides.documentFiles ?? documentFiles,
      onboardingStepStatus: nextStepStatus,
      onboardingResidentId: overrides.onboardingResidentId ?? onboardingResidentId,
      onboardingRegistrationId: overrides.onboardingRegistrationId ?? onboardingRegistrationId,
      assessmentProcessId: overrides.assessmentProcessId ?? assessmentProcessId,
      signedDocumentValidations,
      selectedContactId,
      assessmentFileMeta: assessmentFile
        ? {
            name: assessmentFile.name,
            size: assessmentFile.size,
            type: assessmentFile.type,
          }
        : cachedAssessmentFileMeta,
      assessmentImported,
      assessmentExtraction,
      pendingAssessmentImport,
      formData,
    };
    localStorage.setItem(NEW_RESIDENT_DRAFT_KEY, JSON.stringify(draftPayload));
    setOnboardingStepStatus(nextStepStatus);
    setDraftSavedAt(savedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
  }, [
    assessmentFile,
    assessmentImported,
    assessmentExtraction,
    pendingAssessmentImport,
    cachedAssessmentFileMeta,
    assessmentProcessId,
    contacts,
    currentStep,
    customCareQuestions,
    documentFiles,
    formData,
    onboardingStepStatus,
    otherInsurances,
    onboardingRegistrationId,
    onboardingResidentId,
    selectedContactId,
    signedDocumentValidations,
  ]);
  const resetResidentDraft = () => {
    const confirmed = window.confirm("Clear all New Resident form data and cached draft?");
    if (!confirmed) return;
    localStorage.removeItem(NEW_RESIDENT_DRAFT_KEY);
    setCurrentStep(emptyDraft.currentStep);
    setContacts(emptyDraft.contacts);
    setOtherInsurances(emptyDraft.otherInsurances);
    setCustomCareQuestions(emptyDraft.customCareQuestions);
    setDocumentFiles(emptyDraft.documentFiles);
    setOnboardingStepStatus(emptyDraft.onboardingStepStatus);
    setOnboardingResidentId(emptyDraft.onboardingResidentId);
    setOnboardingRegistrationId(emptyDraft.onboardingRegistrationId);
    setAssessmentProcessId(emptyDraft.assessmentProcessId);
    setSignedDocumentValidations(emptyDraft.signedDocumentValidations);
    setSelectedContactId(emptyDraft.selectedContactId);
    setAssessmentFile(null);
    setCachedAssessmentFileMeta(null);
    setAssessmentImported(emptyDraft.assessmentImported);
    setPendingAssessmentImport(emptyDraft.pendingAssessmentImport);
    setAssessmentExtraction(null);
    setFormData(emptyDraft.formData);
    setSubmitError("");
    setSubmitErrorTitle("Resident was not submitted");
    setDraftSavedAt("");
  };

  useEffect(() => {
    if (onboardingResidentParam) {
      Promise.all([
        initialOnboardingPreviewLoaded
          ? Promise.resolve(initialOnboardingPreview)
          : careService.residents.getOnboardingPreview(onboardingResidentParam),
        careService.residentContacts.listForResident(onboardingResidentParam).catch(() => []),
      ])
        .then(([preview, residentContactList]) => {
          const hydrated = hydrateDraftFromPreview(preview);
          const contactEntries = mergeContactSources(residentContactList, extractResidentContacts(preview)).map(apiContactToForm);
          hydrated.formData = withoutContactFormFields(hydrated.formData);
          contactEntries.forEach((entry) => Object.assign(hydrated.formData, entry.formData));
          const progressIndex = Number(hydrated.onboardingStepStatus?.currentStepIndex) || 1;
          setCurrentStep(progressIndex);
          setContacts(contactEntries.length ? contactEntries.map((entry) => entry.contact) : hydrated.contacts);
          setOtherInsurances(hydrated.otherInsurances);
          setCustomCareQuestions(hydrated.customCareQuestions);
          setDocumentFiles(hydrated.documentFiles);
          setOnboardingStepStatus(hydrated.onboardingStepStatus);
          setOnboardingResidentId(hydrated.onboardingResidentId || onboardingRouteResidentId);
          setOnboardingRegistrationId(hydrated.onboardingRegistrationId);
          setAssessmentProcessId(hydrated.assessmentProcessId || "");
          setSignedDocumentValidations(hydrated.signedDocumentValidations.length ? hydrated.signedDocumentValidations : emptyDraft.signedDocumentValidations);
          setSelectedContactId(contactEntries[0]?.contact?.id || hydrated.contacts[0]?.id || "");
          setCachedAssessmentFileMeta(hydrated.formData?.assessmentFile || null);
          setAssessmentImported(Boolean(hydrated.formData?.assessmentFile?.imported));
          setAssessmentExtraction(hydrated.assessmentExtraction);
          setPendingAssessmentImport(null);
          // The preview request can resolve after the assessment extraction has
          // already populated the form. Preserve any non-empty extracted values
          // when the saved preview does not contain that onboarding stage yet.
          setFormData((current) => mergeImportedFormData(hydrated.formData, current));
          loadedPreviewResidentIdRef.current = String(preview?.residentId || onboardingResidentParam);
          setOnboardingPreview(preview);
          setSubmitError("");
          setSubmitErrorTitle("Resident was not submitted");
        })
        .catch((error) => {
          setSubmitErrorTitle("Resident details could not be loaded");
          setSubmitError(errorMessageFrom(error));
        })
        .finally(() => setDraftLoaded(true));
      return;
    }
    try {
      const cachedDraft = JSON.parse(localStorage.getItem(NEW_RESIDENT_DRAFT_KEY) || "null");
      if (cachedDraft && typeof cachedDraft === "object") {
        const cachedStatus = cachedDraft.onboardingStepStatus && typeof cachedDraft.onboardingStepStatus === "object"
          ? cachedDraft.onboardingStepStatus
          : onboardingStepStatusFor(Number.isInteger(cachedDraft.currentStep) ? cachedDraft.currentStep : 0);
        const cachedProgressIndex = Number(cachedStatus.currentStepIndex) || 0;
        const cachedCurrentStep = Number.isInteger(cachedDraft.currentStep) ? cachedDraft.currentStep : 0;
        setCurrentStep(Math.min(cachedCurrentStep, cachedProgressIndex));
        setContacts(Array.isArray(cachedDraft.contacts) ? cachedDraft.contacts : []);
        setOtherInsurances(Array.isArray(cachedDraft.otherInsurances) ? cachedDraft.otherInsurances : []);
        setCustomCareQuestions(Array.isArray(cachedDraft.customCareQuestions) ? cachedDraft.customCareQuestions : []);
        setDocumentFiles(Array.isArray(cachedDraft.documentFiles) ? cachedDraft.documentFiles : []);
        setOnboardingStepStatus(cachedStatus);
        setOnboardingResidentId(cachedDraft.onboardingResidentId || "");
        setOnboardingRegistrationId(cachedDraft.onboardingRegistrationId || "");
        setAssessmentProcessId(cachedDraft.assessmentProcessId || "");
        setSignedDocumentValidations(Array.isArray(cachedDraft.signedDocumentValidations) ? cachedDraft.signedDocumentValidations : emptyDraft.signedDocumentValidations);
        setSelectedContactId(cachedDraft.selectedContactId || "");
        setCachedAssessmentFileMeta(cachedDraft.assessmentFileMeta || null);
        setAssessmentImported(Boolean(cachedDraft.assessmentImported));
        setAssessmentExtraction(cachedDraft.assessmentExtraction || null);
        setPendingAssessmentImport(cachedDraft.pendingAssessmentImport || null);
        setFormData(cachedDraft.formData && typeof cachedDraft.formData === "object" ? cachedDraft.formData : {});
      }
    } catch {
      localStorage.removeItem(NEW_RESIDENT_DRAFT_KEY);
    } finally {
      setDraftLoaded(true);
    }
  }, [initialOnboardingPreview, initialOnboardingPreviewLoaded, onboardingResidentParam, onboardingRouteResidentId]);

  useEffect(() => {
    if (!embedded) return;
    if (hasInitialAssessmentBioForm) {
      const initialBio = JSON.parse(initialAssessmentBioKey);
      setFormData((current) => ({
        ...current,
        ...initialBio,
      }));
    }
    const loadedInitialContacts = mergeContactSources(initialAssessmentContacts, extractResidentContacts({ previewPayload: { onboardingStages: { bio: initialAssessmentBioData } } }));
    if (loadedInitialContacts.length) {
      const loadedContacts = loadedInitialContacts.map(apiContactToForm);
      setContacts((current) => {
        const mergedContacts = mergeContactSources(current, loadedContacts.map((entry) => entry.contact));
        return mergedContacts;
      });
      setFormData((current) => {
        const next = { ...current };
        loadedContacts.forEach((entry) => Object.assign(next, entry.formData));
        return next;
      });
      setSelectedContactId((current) => current || loadedContacts[0]?.contact?.id || "");
    }
    const initialOtherInsurances = JSON.parse(initialAssessmentOtherInsuranceKey);
    if (Array.isArray(initialOtherInsurances) && initialOtherInsurances.length) {
      setOtherInsurances(initialOtherInsurances);
    }
  }, [embedded, hasInitialAssessmentBioForm, initialAssessmentBioData, initialAssessmentBioKey, initialAssessmentContacts, initialAssessmentOtherInsuranceKey]);

  useEffect(() => {
    if (!embedded || !initialAssessmentExtraction?.sections) return;
    const extractionKey = JSON.stringify({
      fields: extractionFieldsFrom(initialAssessmentExtraction).map((field) => [field.sectionKey, field.label, field.value]).slice(0, 160),
    });
    if (initialAssessmentExtractionKeyRef.current === extractionKey) return;
    initialAssessmentExtractionKeyRef.current = extractionKey;
    const imported = onboardingImportFromAssessmentExtraction(initialAssessmentExtraction);
    setAssessmentExtraction(initialAssessmentExtraction);
    setFormData((current) => replaceImportedFormData(current, {
      ...imported.formData,
      ...imported.contactFormData,
    }));
    if (imported.contacts?.length) {
      setContacts((current) => mergeContactSources(current, imported.contacts));
      setSelectedContactId((current) => current || imported.contacts[0]?.id || "");
    }
    if (imported.customCareQuestions?.length) {
      setCustomCareQuestions((current) => current
        .filter((question) => !String(question.id || "").startsWith("imported-review-question-"))
        .concat(imported.customCareQuestions));
    }
  }, [embedded, initialAssessmentExtraction]);

  useEffect(() => {
    if (otherInsurances.length && formData.hasOtherInsurance !== "Yes") {
      setFormData((current) => ({ ...current, hasOtherInsurance: "Yes" }));
    }
  }, [formData.hasOtherInsurance, otherInsurances.length]);

  useEffect(() => {
    if (!draftLoaded || submitting || currentStep === 0) {
      previousAutoSaveStepRef.current = currentStep;
      return undefined;
    }
    if (previousAutoSaveStepRef.current !== currentStep) {
      previousAutoSaveStepRef.current = currentStep;
      return undefined;
    }
    const timeout = window.setTimeout(() => {
      saveDraft();
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [
    assessmentFile,
    assessmentImported,
    assessmentExtraction,
    cachedAssessmentFileMeta,
    contacts,
    currentStep,
    customCareQuestions,
    documentFiles,
    draftLoaded,
    formData,
    otherInsurances,
    saveDraft,
    selectedContactId,
    signedDocumentValidations,
    submitting,
  ]);

  useEffect(() => {
    if (activeStep !== "review" || !onboardingResidentId) {
      setOnboardingPreview(null);
      return undefined;
    }
    if (loadedPreviewResidentIdRef.current === String(onboardingResidentId)) return undefined;
    let cancelled = false;
    careService.residents.getOnboardingPreview(onboardingResidentId)
      .then((preview) => {
        if (!cancelled) {
          loadedPreviewResidentIdRef.current = String(preview?.residentId || onboardingResidentId);
          setOnboardingPreview(preview);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setSubmitError(errorMessageFrom(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeStep, onboardingResidentId]);

  const goNext = () => setCurrentStep((step) => Math.min(step + 1, intakeSteps.length - 1));
  const uploadAndConfirmDocumentFiles = async () => {
    const uploadedDocuments = [];
    for (const document of documentFiles) {
      if (documentHasStorageFile(document)) {
        const fileId = document.fileId || document.storageFileId;
        const shouldConfirm = !document.confirmed && document.storageStatus !== "CONFIRMED";
        const confirmed = shouldConfirm ? await confirmFileUpload(fileId, actorId) : Boolean(document.confirmed || document.storageStatus === "CONFIRMED");
        uploadedDocuments.push({
          ...document,
          file: undefined,
          fileId,
          confirmed,
          needsUpload: false,
          uploaded: true,
          storageStatus: confirmed ? "CONFIRMED" : "UPLOADED_NOT_CONFIRMED",
        });
        continue;
      }
      if (!documentNeedsStorageUpload(document)) {
        uploadedDocuments.push(document);
        continue;
      }
      const upload = await uploadFile(null, document.file, {
        confirmUpload: true,
        fileType: document.type || document.file?.type || "DOCUMENT",
        includeUserId: false,
        preserveOriginal: true,
        purpose: "OTHER",
        skipUserValidation: true,
      });
      uploadedDocuments.push({
        ...document,
        file: undefined,
        fileId: upload?.fileId || upload?.file?.id || null,
        confirmed: Boolean(upload?.confirmed),
        needsUpload: false,
        uploaded: true,
        uploadedAt: new Date().toISOString(),
        storageStatus: upload?.confirmed ? "CONFIRMED" : "UPLOADED_NOT_CONFIRMED",
      });
    }
    setDocumentFiles(uploadedDocuments);
    return uploadedDocuments;
  };
  const saveCurrentStepToApi = async (nextStepStatus) => {
    const nextDocumentFiles = activeStep === "documents"
      ? withoutAssessmentPageDocuments(await uploadAndConfirmDocumentFiles(), sourceAssessmentPageList)
      : withoutAssessmentPageDocuments(documentFiles, sourceAssessmentPageList);
    const stagePayloads = buildOnboardingStagePayloads({
      assessmentFile,
      assessmentFileMeta: cachedAssessmentFileMeta,
      assessmentImported,
      assessmentExtraction,
      contacts,
      customCareQuestions,
      documentFiles: nextDocumentFiles,
      formData,
      onboardingResidentId,
      onboardingStepStatus: nextStepStatus,
      otherInsurances,
      selectedWorkspace,
      selectedWorkspaceId,
      signedDocumentValidations,
    });
    if (activeStep === "bio") {
      const response = onboardingResidentId
        ? careService.residents.updateOnboardingBio(onboardingResidentId, stagePayloads.bio)
        : careService.residents.createOnboardingBio(stagePayloads.bio);
      return { response: await response, documentFiles: nextDocumentFiles };
    }
    if (!onboardingResidentId) {
      throw new Error("Save Bio Data first so a resident id can be created before continuing.");
    }
    let response = null;
    if (activeStep === "contacts") {
      response = await syncResidentCollateralContacts(onboardingResidentId, stagePayloads.contacts.payload.contacts || []);
      const contactEntries = response.map(apiContactToForm);
      setContacts(contactEntries.map((entry) => entry.contact));
      setFormData((current) => {
        const next = { ...current };
        contactEntries.forEach((entry) => Object.assign(next, entry.formData));
        return next;
      });
      setSelectedContactId((current) => response.some((contact) => contact.id === current) ? current : response[0]?.id || "");
      return { response, documentFiles: nextDocumentFiles };
    }
    if (activeStep === "careHistory") {
      response = await careService.residents.saveOnboardingCareHistory(onboardingResidentId, stagePayloads.careHistory);
      return { response, documentFiles: nextDocumentFiles };
    }
    if (activeStep === "care") {
      response = await careService.residents.saveOnboardingCareSnapshot(onboardingResidentId, stagePayloads.care);
      return { response, documentFiles: nextDocumentFiles };
    }
    if (activeStep === "preliminaryPlan") {
      response = await careService.residents.saveOnboardingCareSnapshot(onboardingResidentId, stagePayloads.preliminaryPlan);
      return { response, documentFiles: nextDocumentFiles };
    }
    if (activeStep === "documents") {
      response = await careService.residents.saveOnboardingDocuments(onboardingResidentId, stagePayloads.documents);
      const returnedDocumentFiles = mergeReturnedDocumentFiles(nextDocumentFiles, response);
      setDocumentFiles(returnedDocumentFiles);
      return { response, documentFiles: returnedDocumentFiles };
    }
    return { response: null, documentFiles: nextDocumentFiles };
  };
  const saveCurrentStep = async () => {
    setSubmitError("");
    setSubmitErrorTitle("Step was not saved");
    setSubmitting(true);
    setSavingAction("save");
    const savedStepStatus = progressStepStatusFor(currentStep);
    try {
      const { response, documentFiles: savedDocumentFiles } = await saveCurrentStepToApi(savedStepStatus);
      const nextResidentId = response?.residentId || onboardingResidentId;
      const nextRegistrationId = response?.registrationId || onboardingRegistrationId;
      const nextAssessmentProcessId = response?.assessmentProcessId || assessmentProcessId;
      const nextStepStatus = response?.onboardingStepStatus || savedStepStatus;
      setOnboardingResidentId(nextResidentId || "");
      setOnboardingRegistrationId(nextRegistrationId || "");
      setAssessmentProcessId(nextAssessmentProcessId || "");
      setOnboardingStepStatus(nextStepStatus);
      saveDraft(currentStep, {
        onboardingResidentId: nextResidentId || "",
        onboardingRegistrationId: nextRegistrationId || "",
        assessmentProcessId: nextAssessmentProcessId || "",
        onboardingStepStatus: nextStepStatus,
        documentFiles: savedDocumentFiles,
        progressStepIndex: Math.max(furthestReachedStep, currentStep),
      });
    } catch (error) {
      setSubmitErrorTitle("Step was not saved");
      setSubmitError(errorMessageFrom(error));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
      setSavingAction("");
    }
  };
  const continueToNextStep = async () => {
    if (activeStep === "source") {
      goNext();
      return;
    }
    const nextStepIndex = Math.min(currentStep + 1, intakeSteps.length - 1);
    setSubmitError("");
    setSubmitErrorTitle("Step was not saved");
    setSubmitting(true);
    setSavingAction("continue");
    const savedStepStatus = progressStepStatusFor(nextStepIndex);
    try {
      const { response, documentFiles: savedDocumentFiles } = await saveCurrentStepToApi(savedStepStatus);
      const nextResidentId = response?.residentId || onboardingResidentId;
      const nextRegistrationId = response?.registrationId || onboardingRegistrationId;
      const nextAssessmentProcessId = response?.assessmentProcessId || assessmentProcessId;
      const nextStepStatus = response?.onboardingStepStatus || savedStepStatus;
      setOnboardingResidentId(nextResidentId || "");
      setOnboardingRegistrationId(nextRegistrationId || "");
      setAssessmentProcessId(nextAssessmentProcessId || "");
      setOnboardingStepStatus(nextStepStatus);
      setCurrentStep(nextStepIndex);
      saveDraft(nextStepIndex, {
        onboardingResidentId: nextResidentId || "",
        onboardingRegistrationId: nextRegistrationId || "",
        assessmentProcessId: nextAssessmentProcessId || "",
        onboardingStepStatus: nextStepStatus,
        documentFiles: savedDocumentFiles,
        progressStepIndex: Math.max(furthestReachedStep, nextStepIndex),
      });
    } catch (error) {
      setSubmitErrorTitle("Step was not saved");
      setSubmitError(errorMessageFrom(error));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
      setSavingAction("");
    }
  };
  const jumpToStep = (index) => {
    if (index > reachableStep) {
      return;
    }
    setCurrentStep(index);
  };
  const goBack = () => setCurrentStep((step) => Math.max(step - 1, 0));
  const openAssessmentUploader = () => assessmentFileRef.current?.click();
  const handleAssessmentFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAssessmentFile(file);
    setCachedAssessmentFileMeta({
      name: file.name,
      size: file.size,
      type: file.type,
    });
    setAssessmentImported(false);
    setAssessmentImportMessage("");
    setAssessmentExtraction(null);
    setPendingAssessmentImport(null);
  };
  const removeAssessmentFile = () => {
    setAssessmentFile(null);
    setCachedAssessmentFileMeta(null);
    setAssessmentImported(false);
    setAssessmentImportMessage("");
    setAssessmentExtraction(null);
    setPendingAssessmentImport(null);
    if (assessmentFileRef.current) {
      assessmentFileRef.current.value = "";
    }
  };
  const importAssessment = async () => {
    if (!assessmentFile) {
      openAssessmentUploader();
      return;
    }
    setSubmitError("");
    setSubmitErrorTitle("Assessment import failed");
    setAssessmentImportMessage("");
    setAssessmentImporting(true);
    try {
      const extraction = assessmentProcessId
        ? await careService.assessmentExtractions.extractForProcess(assessmentProcessId, [assessmentFile])
        : await careService.residents.importOnboardingAssessment([assessmentFile]);
      const imported = onboardingImportFromAssessmentExtraction(extraction);
      setAssessmentExtraction(extraction);
      setPendingAssessmentImport({ extraction, imported });
      setAssessmentImported(false);
      setAssessmentImportMessage(
        assessmentProcessId
          ? `Extracted and saved ${extractionFieldsFrom(extraction).length} assessment fields to the resident assessment. Review the mapped data below, then apply it to the resident form.`
          : `Extracted ${extractionFieldsFrom(extraction).length} assessment fields. Review the mapped data below, then apply it to the resident form.`,
      );
    } catch (error) {
      setAssessmentImported(false);
      setPendingAssessmentImport(null);
      setAssessmentExtraction(null);
      setSubmitErrorTitle("Assessment import failed");
      setSubmitError(errorMessageFrom(error));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setAssessmentImporting(false);
    }
  };
  const applyPendingAssessmentImport = () => {
    if (!pendingAssessmentImport?.imported) return;
    const { extraction, imported } = pendingAssessmentImport;
    setSubmitError("");
    setSubmitErrorTitle("Resident was not submitted");
    setAssessmentExtraction(extraction);
    setFormData((current) => replaceImportedFormData(current, {
      ...imported.formData,
      ...imported.contactFormData,
    }));
    setContacts(imported.contacts);
    setSelectedContactId(imported.contacts[0]?.id || "");
    setCustomCareQuestions((current) => current
      .filter((question) => !String(question.id || "").startsWith("imported-review-question-"))
      .concat(imported.customCareQuestions));
    setPendingAssessmentImport(null);
    setAssessmentImported(true);
    setAssessmentImportMessage(`Applied ${extractionFieldsFrom(extraction).length} extracted assessment fields. Review Bio Data through Preliminary Plan before submitting.`);
    setOnboardingStepStatus(onboardingStepStatusFor(1, new Date().toISOString(), "IN_PROGRESS"));
    setCurrentStep(1);
  };
  const clearPendingAssessmentImport = () => {
    setPendingAssessmentImport(null);
    setAssessmentImported(false);
    setAssessmentImportMessage("");
  };
  const updateField = (field, value) => setFormData((current) => ({ ...current, [field]: value }));
  const selectedContact = contacts.find((contact) => contact.id === selectedContactId) || contacts[0];
  const addContact = () => {
    const timestamp = Date.now();
    setContacts((current) => {
      const nextNumber = current.length + 1;
      const nextContact = {
        id: `contact-${timestamp}-${nextNumber}`,
        prefix: `residentContact${timestamp}${nextNumber}`,
        type: "",
        notes: "",
      };
      setSelectedContactId(nextContact.id);
      return [
        ...current,
        nextContact,
      ];
    });
  };
  const updateContact = (id, field, value) => {
    setContacts((current) => current.map((contact) => (contact.id === id ? { ...contact, [field]: value } : contact)));
  };
  const removeContact = (id) => {
    setContacts((current) => {
      const next = current.filter((contact) => contact.id !== id);
      if (selectedContactId === id) {
        setSelectedContactId(next[0]?.id || "");
      }
      return next;
    });
  };
  const addOtherInsurance = () => {
    const timestamp = Date.now();
    setOtherInsurances((current) => [
      ...current,
      {
        id: `other-insurance-${timestamp}-${current.length + 1}`,
        provider: "",
        policyNumber: "",
        groupNumber: "",
        notes: "",
      },
    ]);
  };
  const updateOtherInsurance = (id, field, value) => {
    setOtherInsurances((current) => current.map((insurance) => (insurance.id === id ? { ...insurance, [field]: value } : insurance)));
  };
  const removeOtherInsurance = (id) => {
    setOtherInsurances((current) => current.filter((insurance) => insurance.id !== id));
  };
  const addCustomCareQuestion = () => {
    const timestamp = Date.now();
    setCustomCareQuestions((current) => [
      ...current,
      {
        id: `care-question-${timestamp}-${current.length + 1}`,
        question: "",
        response: "",
      },
    ]);
  };
  const updateCustomCareQuestion = (id, field, value) => {
    setCustomCareQuestions((current) => current.map((question) => (question.id === id ? { ...question, [field]: value } : question)));
  };
  const removeCustomCareQuestion = (id) => {
    setCustomCareQuestions((current) => current.filter((question) => question.id !== id));
  };
  const addDocumentFiles = (event) => {
    const files = Array.from(event.target.files || []).map((file) => {
      const localPreviewUrl = URL.createObjectURL(file);
      documentPreviewUrlsRef.current.add(localPreviewUrl);
      return {
        id: `resident-document-${Date.now()}-${file.name}`,
        file,
        fileName: file.name,
        name: file.name,
        description: "",
        type: file.type,
        size: file.size,
        localPreviewUrl,
        previewUrl: localPreviewUrl,
        uploaded: false,
        confirmed: false,
        fileId: null,
        needsUpload: true,
        storageStatus: "PENDING_UPLOAD",
      };
    });
    if (!files.length) return;
    setDocumentFiles((current) => [...current, ...files]);
    event.target.value = "";
  };
  const updateDocumentFile = (id, field, value) => {
    setDocumentFiles((current) => current.map((document) => (document.id === id ? { ...document, [field]: value } : document)));
  };
  const removeDocumentFile = (id) => {
    setDocumentFiles((current) => current.filter((document) => {
      if (document.id === id && document.localPreviewUrl) {
        URL.revokeObjectURL(document.localPreviewUrl);
        documentPreviewUrlsRef.current.delete(document.localPreviewUrl);
      }
      return document.id !== id;
    }));
  };
  const previewEntryForDocument = (document, fallbackName = "Document preview") => {
    const url = documentPreviewUrl(document, documentSignedUrls) || documentDownloadUrl(document, documentSignedUrls);
    if (!url) return null;
    return {
      document,
      name: documentPreviewName(document, fallbackName),
      type: document.type || document.mimeType || "",
      url,
      isImage: isImageDocument(document),
      previewError: isImageDocument(document) && !isBrowserPreviewableImage(document),
    };
  };
  const openDocumentPreview = (document) => {
    const preview = previewEntryForDocument(document);
    if (!preview) return;
    setFilePreview({
      ...preview,
      entries: [preview],
      currentIndex: 0,
      title: preview.name,
    });
    setFilePreviewRotation(0);
  };
  const openSourceAssessmentGallery = (index) => {
    const entries = sourceAssessmentPageList
      .map((page, pageIndex) => previewEntryForDocument(page, `Page ${pageIndex + 1}`))
      .filter(Boolean);
    if (!entries.length) return;
    const page = sourceAssessmentPageList[index];
    const selectedUrl = documentPreviewUrl(page, documentSignedUrls) || documentDownloadUrl(page, documentSignedUrls);
    const selectedIndex = Math.max(0, entries.findIndex((entry) => entry.document === page || entry.url === selectedUrl));
    const activeEntry = entries[selectedIndex] || entries[0];
    setFilePreview({
      ...activeEntry,
      entries,
      currentIndex: selectedIndex > -1 ? selectedIndex : 0,
      title: "Initial state assessment pages",
    });
    setFilePreviewRotation(0);
  };
  const closeFilePreview = () => {
    setFilePreview(null);
    setFilePreviewRotation(0);
  };
  const showFilePreviewEntry = (nextIndex) => {
    setFilePreview((current) => {
      const entries = current?.entries || [];
      if (!entries.length) return current;
      const normalizedIndex = (nextIndex + entries.length) % entries.length;
      return {
        ...entries[normalizedIndex],
        entries,
        currentIndex: normalizedIndex,
        title: current.title,
      };
    });
    setFilePreviewRotation(0);
  };
  const addSignedDocumentValidation = () => {
    const timestamp = Date.now();
    setSignedDocumentValidations((current) => [
      ...current,
      {
        id: `signed-custom-${timestamp}`,
        documentName: "",
        signedVerified: false,
        fileUploaded: false,
        notes: "",
      },
    ]);
  };
  const updateSignedDocumentValidation = (id, field, value) => {
    setSignedDocumentValidations((current) => current.map((document) => (document.id === id ? { ...document, [field]: value } : document)));
  };
  const removeSignedDocumentValidation = (id) => {
    setSignedDocumentValidations((current) => current.filter((document) => document.id !== id));
  };
  const handleSubmitResident = async () => {
    setSubmitError("");
    setSubmitErrorTitle("Resident was not submitted");
    setSubmitting(true);
    try {
      if (!onboardingResidentId) {
        throw new Error("Save Bio Data first so the resident can be created before submitting.");
      }
      const submittedStatus = onboardingStepStatusFor(currentStep, new Date().toISOString(), "ACTIVE");
      const response = await careService.residents.confirmOnboarding(onboardingResidentId, {
        workspaceId: selectedWorkspaceId || null,
        organizationId: selectedWorkspace?.organizationId || null,
        onboardingStepStatus: submittedStatus,
        payload: {
          residentId: onboardingResidentId,
          registrationId: onboardingRegistrationId || onboardingPreview?.registrationId || null,
          confirmed: true,
          confirmedAt: submittedStatus.lastSavedAt,
          previewSections,
          previewPayload: onboardingPreview?.previewPayload || null,
        },
      });
      const nextAssessmentProcessId = response?.assessmentProcessId || "";
      setAssessmentProcessId(nextAssessmentProcessId);
      localStorage.removeItem(NEW_RESIDENT_DRAFT_KEY);
      setOnboardingStepStatus(response?.onboardingStepStatus || submittedStatus);
      setOnboardingPreview((current) => ({
        ...(current || {}),
        residentId: onboardingResidentId,
        registrationId: response?.registrationId || onboardingRegistrationId || current?.registrationId || null,
        assessmentProcessId: nextAssessmentProcessId || current?.assessmentProcessId || "",
        onboardingStepStatus: response?.onboardingStepStatus || submittedStatus,
        previewPayload: response?.payload || current?.previewPayload || null,
      }));
      setSubmitError("");
      setDraftSavedAt("");
      window.scrollTo({ top: 0, behavior: "smooth" });
      refreshApplicationSoon();
    } catch (error) {
      setSubmitErrorTitle("Resident was not submitted");
      setSubmitError(errorMessageFrom(error));
      setCurrentStep(intakeSteps.length - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };
  const confirmSubmitResident = () => {
    Modal.confirm({
      title: "Confirm resident onboarding details?",
      content: (
        <div>
          <p>This will confirm the resident onboarding details and start the resident care process.</p>
          <p>After confirmation, these details cannot be changed from this onboarding flow.</p>
        </div>
      ),
      okText: "Confirm details",
      cancelText: "Review again",
      onOk: handleSubmitResident,
    });
  };
  const previewValue = (value) => value || "Not added";
  const previewRowIsWide = (label) => /address|notes|description|details|condition|diagnosis|cause|snapshot|care needed|behavior|document/i.test(label || "");
  const previewValueIsRich = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ""));
  const previewSections = [
    {
      title: "Personal Bio Data",
      icon: "ti-user-circle",
      tone: "bio",
      rows: [
        ["Full name", [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(" ")],
        ["Preferred name", formData.preferredName],
        ["Date of birth", formData.dateOfBirth],
        ["Social Security Number", formData.socialSecurityNumber],
        ["Race", formData.race],
        ["Place of Birth", formData.placeOfBirth],
        ["Religion", formData.religion],
        ["Move-in Date", formData.admissionDate],
        ["Height", formData.heightFeet ? `${formData.heightFeet} ft${formData.heightInches !== "" && formData.heightInches != null ? ` ${formData.heightInches} in` : ""}` : ""],
        ["Weight", formData.weightLbs ? `${formData.weightLbs} lbs` : ""],
        ["Sex", formData.sex],
        ["Email", formData.email],
        ["Mobile phone", formData.mobilePhone],
        ["Preferred phone", preferredPhoneLabel(formData.preferredPhone)],
      ],
    },
    {
      title: "Address",
      icon: "ti-map-pin",
      tone: "address",
      rows: [
        ["Address type", formData.addressType === "Other" ? formData.addressTypeOther : formData.addressType],
        ["Street address", formData.address],
        ["City", formData.city],
        ["County", formData.county],
        ["State", formData.state],
        ["Country", formData.country],
        ["Zip Code", formData.zip],
      ],
    },
    {
      title: "Coverage and Directives",
      icon: "ti-shield-check",
      tone: "coverage",
      rows: [
        ["Code status", formData.codeStatus],
        ["Advanced directives", formData.advancedDirectives],
        ["Medicare Number", formData.medicareNumber],
        ["Medicaid Number", formData.medicaidNumber],
        ["Other Supplementary Insurance", formData.hasOtherInsurance],
        ...otherInsurances.map((insurance, index) => [
          `Other insurance ${index + 1}`,
          otherInsuranceFields
            .map(([field, label]) => (insurance[field] ? `${label}: ${insurance[field]}` : ""))
            .filter(Boolean)
            .join(" | "),
        ]),
      ],
    },
    {
      title: "Care History",
      icon: "ti-history",
      tone: "history",
      rows: [
        ["Admitted at a hospital", formData.wasAdmittedAtHospital],
        ...admissionDetailsFields.map(([field, label]) => [`Hospital admission - ${label}`, formData[field]]),
        ["Hospital admission - Address", prefixedAddressSummary(formData, "hospital")],
        ["Hospital admission - Condition, diagnosis, and cause", formData.admissionDescription],
        ["Coming from another family home", formData.fromAnotherFamilyHome],
        ...priorFamilyHomeFields.map(([field, label]) => [`Prior family home - ${label}`, formData[field]]),
        ["Prior family home - Address", prefixedAddressSummary(formData, "priorFamilyHome")],
        ["Prior family home - Details", formData.priorFamilyHomeDetails],
        ["Had prior caregiver", formData.hadPriorCaregiver],
        ...priorCaregiverFields.map(([field, label]) => [`Prior caregiver - ${label}`, formData[field]]),
        ["Prior caregiver - Address", prefixedAddressSummary(formData, "priorCaregiver")],
        ["Prior caregiver - Care details", formData.priorCaregiverDetails],
        ["Background notes", formData.bioNotes],
      ],
    },
    {
      title: "Contacts and Decision Makers",
      icon: "ti-address-book",
      tone: "contacts",
      rows: [
        ...contacts.map((contact, index) => [
          `${contact.type === "Other" ? contact.relationshipOther || "Other" : contact.type || "Contact"} ${index + 1}`,
          [contact.notes ? `Notes: ${contact.notes}` : "", contactSummary(formData, contact.prefix)].filter(Boolean).join(" | "),
        ]),
      ],
    },
    {
      title: "Initial Care Snapshot",
      icon: "ti-heart-rate-monitor",
      tone: "care",
      rows: [
        ["Known diagnosis", formData.diagnosis],
        ["Allergies", formData.allergies],
        ["Diet", formData.diet],
        ["Medication notes", formData.medicationNotes],
        ["Care needed", formData.careNeeded],
        ["Behavior and safety notes", formData.behaviorSafetyNotes],
        ...customCareQuestions.map((question, index) => [
          question.question || `Custom question ${index + 1}`,
          question.response,
        ]),
      ],
    },
    {
      title: "Preliminary Plan",
      icon: "ti-heart-handshake",
      tone: "care",
      rows: [
        ["Resident goals", formData.preliminaryPlanGoals],
        ["Care approach", formData.preliminaryPlanCareApproach],
        ["Staff instructions", formData.preliminaryPlanStaffInstructions],
        ["Family preferences", formData.preliminaryPlanFamilyPreferences],
        ["Risks and follow-up", formData.preliminaryPlanRisksAndFollowUp],
      ],
    },
    {
      title: "Documents and Signing",
      icon: "ti-file-check",
      tone: "documents",
      rows: [
        ["Uploaded documents", documentFiles.length ? `${documentFiles.length} file${documentFiles.length === 1 ? "" : "s"} attached` : ""],
        ...documentFiles.map((document, index) => [
          `Document ${index + 1}`,
          [document.name || document.fileName, document.description].filter(Boolean).join(" | "),
        ]),
        ...signedDocumentValidations.map((document) => [
          document.documentName || "Signed document",
          `Signed verified: ${document.signedVerified ? "Yes" : "No"} | File uploaded: ${document.fileUploaded ? "Yes" : "No"}${document.notes ? ` | Notes: ${document.notes}` : ""}`,
        ]),
      ],
    },
  ];
  const residentFullName = [formData.firstName, formData.middleName, formData.lastName].filter(Boolean).join(" ");
  const residentAddressSummary = [formData.address, formData.city, formData.state, formData.zip].filter(Boolean).join(", ");
  const hasSavedBioData = Boolean(onboardingResidentId) || furthestReachedStep >= 1;
  const resumeBioDetails = [
    ["Name", residentFullName || "Not added"],
    ["Preferred", formData.preferredName || "Not added"],
    ["Sex", formData.sex || "Not added"],
    ["Details", residentAddressSummary || formData.email || formData.mobilePhone || "Not added"],
  ];
  const onboardingStatusValue = String(onboardingStepStatus?.status || onboardingPreview?.onboardingStepStatus?.status || "").toUpperCase();
  const residentStatusValue = String(onboardingPreview?.resident?.status || onboardingPreview?.previewPayload?.onboardingStages?.resident?.status || "").toUpperCase();
  const isResidentActive = ["ACTIVE", "CONFIRMED", "ONBOARDED"].includes(onboardingStatusValue) || residentStatusValue === "ACTIVE";
  const activeResidentId = onboardingResidentId || onboardingPreview?.residentId || "";
  const activeAssessmentProcessId = assessmentProcessId || onboardingPreview?.assessmentProcessId || "";
  const showActiveResidentLink = Boolean(activeResidentId && ["ACTIVE", "SUBMITTED", "COMPLETED"].includes(onboardingStatusValue));
  const showAssessmentProcessLink = Boolean(activeAssessmentProcessId && ["ACTIVE", "SUBMITTED", "COMPLETED"].includes(onboardingStatusValue));
  const pendingImportGroups = pendingAssessmentImport
    ? importedPrefillGroups(pendingAssessmentImport.imported, pendingAssessmentImport.extraction)
    : [];

  return (
    <CareShell embedded={embedded} className="dashboard-content new-resident-page">
      <div className="crumbs">Residents <i className="ti ti-chevron-right" /> New Resident</div>
      <div className="new-resident-hero">
        <div>
          <span className="section-eyebrow">{isResidentActive ? "Onboarding Details" : "Resident Intake"}</span>
          <h1>{isResidentActive ? "Resident Onboarded Successfully" : "Onboarding New Resident"}</h1>
          <p>
            {isResidentActive
              ? "The resident onboarding requirements are confirmed. No more onboarding action is required."
              : "Enter resident details, contacts, care history, care snapshot, and required documents to complete onboarding."}
          </p>
        </div>
        <div className="new-resident-actions">
          <Link className="mini-btn" to="/residents"><i className="ti ti-arrow-left" /> Residents</Link>
          <button className="mini-btn danger" type="button" onClick={resetResidentDraft}><i className="ti ti-refresh" /> Reset</button>
        </div>
      </div>
      {draftSavedAt ? <div className="resident-draft-status">Draft saved {draftSavedAt}</div> : null}
      {showActiveResidentLink || showAssessmentProcessLink ? (
        <div className="resident-onboarding-assessment-link">
          <span>
            <strong>Resident onboarded successfully</strong>
            <small>No more action is required in the onboarding requirements.</small>
          </span>
          <div className="resident-onboarding-link-actions">
            {activeResidentId ? (
              <Link className="btn primary" to={`/residents/${activeResidentId}`}>
                <i className="ti ti-user-circle" /> Resident Details
              </Link>
            ) : null}
            {showAssessmentProcessLink ? (
              <Link className="btn primary" to={`/assessments/${activeAssessmentProcessId}/new`}>
                <i className="ti ti-clipboard-text" /> Open Assessment
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
      {onboardingStepStatus?.canResume ? (
        <div className={`resident-onboarding-status${hasSavedBioData ? " has-bio-summary" : ""}`} role="status">
          <div className="resident-onboarding-status-main">
            <i className="ti ti-player-play" />
            <span>
              <strong>Resume onboarding at {onboardingStepStatus.currentStepLabel}</strong>
              <small>
                Status: {onboardingStepStatus.status.replaceAll("_", " ").toLowerCase()}
                {onboardingStepStatus.lastSavedAt ? ` | Last saved ${new Date(onboardingStepStatus.lastSavedAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}` : ""}
              </small>
            </span>
          </div>
          {hasSavedBioData ? (
            <dl className="resident-onboarding-bio-summary" aria-label="Saved resident bio data">
              {resumeBioDetails.map(([label, value]) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      ) : null}

      <nav className="resident-intake-steps" aria-label="New resident intake steps">
        {intakeSteps.map(([key, title, text], index) => (
          <button
            aria-disabled={index > reachableStep}
            className={`${index === currentStep ? "is-active" : ""}${index < reachableStep ? " is-complete" : ""}${index > reachableStep ? " is-locked" : ""}`}
            disabled={index > reachableStep}
            key={key}
            onClick={() => jumpToStep(index)}
            type="button"
          >
            <b>{index + 1}</b>
            <span><strong>{title}</strong><small>{text}</small></span>
          </button>
        ))}
      </nav>

      {submitError ? (
        <div className="resident-submit-error" role="alert">
          <i className="ti ti-alert-circle" />
          <span>
            <strong>{submitErrorTitle}</strong>
            <small>{submitError}</small>
          </span>
        </div>
      ) : null}

      {activeStep === "source" && (
        <div className="new-resident-grid">
          <Panel title="Start Manually">
            <div className="resident-start-card">
              <span className="round-icon blue"><i className="ti ti-keyboard" /></span>
              <h3>Manual resident intake</h3>
              <p>Use guided sections to enter bio data, contacts, diagnosis, medication notes, and initial care details yourself.</p>
              <button className="mini-btn" type="button" onClick={goNext}>Enter bio data <i className="ti ti-arrow-right" /></button>
            </div>
          </Panel>

          <Panel title="Import State Assessment">
            <div className="assessment-import-card">
              {sourceAssessmentPageList.length ? (
                renderReadOnlyAssessmentPages()
              ) : (
                <>
                  {!displayedAssessmentFile ? (
                    <button className="assessment-upload" type="button" onClick={openAssessmentUploader}>
                      <i className="ti ti-file-upload" />
                      <strong>Upload assessment from state</strong>
                      <span>PDF, DOC, DOCX, or image assessment file</span>
                    </button>
                  ) : null}
                  <input
                    ref={assessmentFileRef}
                    className="assessment-file-input"
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp,image/heic,image/heif"
                    onChange={handleAssessmentFile}
                  />
                  {displayedAssessmentFile ? (
                    <div className="assessment-file-selected">
                      <i className={`ti ${assessmentImporting ? "ti-loader-2" : assessmentImported || pendingAssessmentImport ? "ti-circle-check" : "ti-file-check"}`} />
                      <span>
                        <strong>{displayedAssessmentFile.name}</strong>
                        <small>
                          {assessmentImporting
                            ? "Uploading and extracting assessment data..."
                            : pendingAssessmentImport
                              ? "Extraction ready. Review the mapped data before applying it."
                              : assessmentImported
                                ? "Extract successful. Review Bio Data through Preliminary Plan."
                                : "Attached. Extract data or remove this file."}
                        </small>
                      </span>
                      <div className="assessment-file-actions">
                        <button className="mini-btn danger" type="button" onClick={removeAssessmentFile} disabled={assessmentImporting}><i className="ti ti-trash" /> Remove file</button>
                        <button className="mini-btn" type="button" onClick={importAssessment} disabled={assessmentImporting}><i className={`ti ${assessmentImporting ? "ti-loader-2" : "ti-scan"}`} /> {assessmentImporting ? "Extracting..." : "Extract data"}</button>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
              {assessmentImportMessage ? (
                <div className="resident-submit-success">
                  <i className="ti ti-circle-check" />
                  <span>{assessmentImportMessage}</span>
                </div>
              ) : null}
              {pendingAssessmentImport ? (
                <div className="assessment-prefill-review">
                  <div className="assessment-prefill-review-head">
                    <span>
                      <strong>Review extracted data before prefilling</strong>
                      <small>Only these mapped values will be applied to the resident intake fields.</small>
                    </span>
                    <div className="assessment-file-actions">
                      <button className="mini-btn danger" type="button" onClick={clearPendingAssessmentImport} disabled={assessmentImporting}>
                        <i className="ti ti-x" /> Clear
                      </button>
                      <button className="mini-btn" type="button" onClick={applyPendingAssessmentImport} disabled={assessmentImporting}>
                        <i className="ti ti-check" /> Apply to form
                      </button>
                    </div>
                  </div>
                  {pendingImportGroups.length ? (
                    <div className="assessment-prefill-review-grid">
                      {pendingImportGroups.map((group) => (
                        <section className="assessment-prefill-review-card" key={group.title}>
                          <h4>{group.title}</h4>
                          <dl>
                            {group.rows.slice(0, 10).map(([label, value], index) => (
                              <div key={`${group.title}-${label}-${index}`}>
                                <dt>{label}</dt>
                                <dd>{value}</dd>
                              </div>
                            ))}
                          </dl>
                          {group.rows.length > 10 ? <small>{group.rows.length - 10} more mapped values will be applied.</small> : null}
                        </section>
                      ))}
                    </div>
                  ) : (
                    <p className="resident-inline-empty">No mapped fields were found for the resident form. You can re-extract or continue manually.</p>
                  )}
                </div>
              ) : null}
              <div className="assessment-import-summary">
                <strong>Information to extract</strong>
                {assessmentItems.map((item) => (
                  <span key={item}><i className="ti ti-check" /> {item}</span>
                ))}
              </div>
              {!displayedAssessmentFile && !sourceAssessmentPageList.length ? (
                <button className="mini-btn full" type="button" onClick={openAssessmentUploader}><i className="ti ti-file-upload" /> Import assessment</button>
              ) : null}
            </div>
          </Panel>
        </div>
      )}

      {activeStep === "bio" && (
        <Panel title="Personal Bio Data">
          <div className="resident-form-sections">
            <div className="resident-form-main-column">
              <section className="resident-form-card">
                <h3>Resident Identifiers</h3>
                <div className="resident-form-grid compact">
                  {bioFields.slice(0, 6).map(([name, label, type]) => (
                    <label className="resident-form-field" key={name}>
                      <span>{label}</span>
                      {name === "socialSecurityNumber" ? (
                        <div className="resident-sensitive-input">
                          <input
                            autoComplete="off"
                            inputMode="numeric"
                            maxLength={11}
                            name={name}
                            type={showSsn ? "text" : "password"}
                            value={formData[name] || ""}
                            onChange={(event) => updateField(name, formatSocialSecurityNumber(event.target.value))}
                          />
                          <button
                            aria-label={showSsn ? "Hide Social Security Number" : "Show Social Security Number"}
                            onClick={() => setShowSsn((current) => !current)}
                            type="button"
                          >
                            <i className={`ti ${showSsn ? "ti-eye-off" : "ti-eye"}`} />
                          </button>
                        </div>
                      ) : (
                        <input name={name} type={type} value={formData[name] || ""} onChange={(event) => updateField(name, event.target.value)} />
                      )}
                    </label>
                  ))}
                  <label className="resident-form-field">
                    <span>Sex</span>
                    <select name="sex" value={formData.sex || ""} onChange={(event) => updateField("sex", event.target.value)}>
                      <option value="">Select sex</option>
                      {sexOptions.map((option) => (
                        <option value={option} key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="resident-form-field">
                    <span>Race</span>
                    <select name="race" value={formData.race || ""} onChange={(event) => updateField("race", event.target.value)}>
                      <option value="">Select race</option>
                      {raceOptions.map((option) => <option value={option} key={option}>{option}</option>)}
                    </select>
                  </label>
                  {formData.race === "Other" ? <label className="resident-form-field"><span>Specify race</span><input value={formData.raceOther || ""} onChange={(event) => updateField("raceOther", event.target.value)} /></label> : null}
                  <label className="resident-form-field"><span>Marital status</span><select value={formData.maritalStatus || ""} onChange={(event) => updateField("maritalStatus", event.target.value)}><option value="">Select marital status</option>{maritalStatusOptions.map((option) => <option value={option} key={option}>{option}</option>)}</select></label>
                  <label className="resident-form-field">
                    <span>Height (ft-in)</span>
                    <span className="resident-height-fields">
                      <input aria-label="Height feet" inputMode="numeric" max="9" min="1" placeholder="ft" type="number" value={formData.heightFeet ?? ""} onChange={(event) => updateField("heightFeet", event.target.value)} />
                      <input aria-label="Height inches" inputMode="numeric" max="11" min="0" placeholder="in" type="number" value={formData.heightInches ?? ""} onChange={(event) => updateField("heightInches", event.target.value)} />
                    </span>
                  </label>
                  <label className="resident-form-field">
                    <span>Weight (lbs)</span>
                    <input inputMode="decimal" min="1" step="0.1" type="number" value={formData.weightLbs ?? ""} onChange={(event) => updateField("weightLbs", event.target.value)} />
                  </label>
                  <label className="resident-form-field"><span>Place of Birth</span><input value={formData.placeOfBirth || ""} onChange={(event) => updateField("placeOfBirth", event.target.value)} /></label>
                  <label className="resident-form-field"><span>Religion</span><input value={formData.religion || ""} onChange={(event) => updateField("religion", event.target.value)} /></label>
                </div>
              </section>

              <section className="resident-form-card">
                <h3>Status</h3>
                <div className="resident-form-grid compact">
                  <label className="resident-form-field">
                    <span>Code Status</span>
                    <select name="codeStatus" value={formData.codeStatus || ""} onChange={(event) => updateField("codeStatus", event.target.value)}>
                      <option value="">Select code status</option>
                      {codeStatusOptions.map((option) => (
                        <option value={option} key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="resident-form-field">
                    <span>Advanced Directives</span>
                    <select name="advancedDirectives" value={formData.advancedDirectives || ""} onChange={(event) => updateField("advancedDirectives", event.target.value)}>
                      <option value="">Select option</option>
                      {yesNoOptions.map((option) => (
                        <option value={option} key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </section>

              <section className="resident-form-card">
                <h3>Resident Contact Information</h3>
                <div className="resident-form-grid compact">
                  {phoneFields.map(([name, label]) => (
                    <label className="resident-form-field" key={name}>
                      <span>{label}</span>
                      <input name={name} type="tel" value={formData[name] || ""} onChange={(event) => updateField(name, event.target.value)} />
                    </label>
                  ))}
                  <label className="resident-form-field">
                    <span>Preferred phone</span>
                    <select name="preferredPhone" value={formData.preferredPhone || ""} onChange={(event) => updateField("preferredPhone", event.target.value)}>
                      <option value="">Select preferred</option>
                      {preferredPhoneOptions.map(([value, label]) => (
                        <option value={value} key={value}>{label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="resident-form-field">
                    <span>Email</span>
                    <input name="email" type="email" value={formData.email || ""} onChange={(event) => updateField("email", event.target.value)} />
                  </label>
                  <label className="resident-form-field full-span resident-address-search">
                    <span>Location search</span>
                    {appConfigLoading && !googleMapsApiKey ? (
                      <input type="text" disabled placeholder="Loading map address search..." />
                    ) : (
                      <GooglePlacesAutocompleteInput
                        googleMapsApiKey={googleMapsApiKey}
                        placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
                        types={["address"]}
                        value={formData.addressSearch || formData.address || ""}
                        onChange={(value) => {
                          updateField("addressSearch", value);
                          if (!googleMapsApiKey) updateField("address", value);
                        }}
                        onResolvePlace={async ({ details, prediction }) => {
                          const resolvedAddress = details?.formatted_address || prediction?.description || formData.addressSearch || "";
                          updateField("addressSearch", resolvedAddress);
                          updateField("address", resolvedAddress);
                          if (details) {
                            const fields = addressFieldsFromPlaceDetails(details);
                            updateField("address", fields.addressLine1 || resolvedAddress);
                            updateField("city", fields.city || "");
                            updateField("county", fields.county || "");
                            updateField("state", fields.state || "");
                            updateField("country", fields.country || "");
                            updateField("zip", fields.zipCode || "");
                            updateField("latitude", fields.latitude || "");
                            updateField("longitude", fields.longitude || "");
                            updateField("placeId", fields.placeId || "");
                          }
                        }}
                      />
                    )}
                  </label>
                  <label className="resident-form-field">
                    <span>Address type</span>
                    <select name="addressType" value={formData.addressType || ""} onChange={(event) => updateField("addressType", event.target.value)}>
                      <option value="">Select address type</option>
                      {addressTypeOptions.map((option) => (
                        <option value={option} key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  {formData.addressType === "Other" ? (
                    <label className="resident-form-field">
                      <span>Specify address type</span>
                      <input value={formData.addressTypeOther || ""} onChange={(event) => updateField("addressTypeOther", event.target.value)} />
                    </label>
                  ) : null}
                  <label className="resident-form-field">
                    <span>Address line 1</span>
                    <input value={formData.address || ""} onChange={(event) => updateField("address", event.target.value)} />
                  </label>
                  <label className="resident-form-field">
                    <span>Address line 2</span>
                    <input value={formData.addressLine2 || ""} onChange={(event) => updateField("addressLine2", event.target.value)} />
                  </label>
                  <label className="resident-form-field">
                    <span>City</span>
                    <input value={formData.city || ""} onChange={(event) => updateField("city", event.target.value)} />
                  </label>
                  <label className="resident-form-field">
                    <span>County</span>
                    <input value={formData.county || ""} onChange={(event) => updateField("county", event.target.value)} />
                  </label>
                  <label className="resident-form-field">
                    <span>State</span>
                    <input value={formData.state || ""} onChange={(event) => updateField("state", event.target.value)} />
                  </label>
                  <label className="resident-form-field">
                    <span>Zip code</span>
                    <input value={formData.zip || ""} onChange={(event) => updateField("zip", event.target.value)} />
                  </label>
                  <label className="resident-form-field">
                    <span>Country</span>
                    <input value={formData.country || ""} onChange={(event) => updateField("country", event.target.value)} />
                  </label>
                </div>
              </section>

              <section className="resident-form-card">
                <h3>Insurance</h3>
              <div className="resident-form-grid compact">
                {insuranceFields.map(([name, label]) => (
                  <label className="resident-form-field" key={name}>
                    <span>{label}</span>
                    <input name={name} type="text" value={formData[name] || ""} onChange={(event) => updateField(name, event.target.value)} />
                  </label>
                ))}
                <label className="resident-form-field">
                  <span>Do you have other Supplementary Insurance?</span>
                  <select
                    name="hasOtherInsurance"
                    value={hasSupplementaryInsurance ? "Yes" : formData.hasOtherInsurance || ""}
                    onChange={(event) => {
                      updateField("hasOtherInsurance", event.target.value);
                      if (event.target.value !== "Yes") {
                        setOtherInsurances([]);
                      }
                    }}
                  >
                    <option value="">Select option</option>
                    {yesNoOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
              </div>
              {hasSupplementaryInsurance ? (
                <div className="resident-other-insurance">
                  <div className="resident-other-insurance-head">
                    <span>Other insurance records</span>
                    <button className="mini-btn resident-add-insurance-btn" type="button" onClick={addOtherInsurance}>
                      <i className="ti ti-plus" /> Add Insurance
                    </button>
                  </div>
                  {otherInsurances.length ? (
                    <div className="resident-other-insurance-list">
                      {otherInsurances.map((insurance, index) => (
                        <article className="resident-other-insurance-card" key={insurance.id}>
                          <div className="resident-other-insurance-title">
                            <strong>Other Insurance {index + 1}</strong>
                            <button aria-label="Remove insurance" type="button" onClick={() => removeOtherInsurance(insurance.id)}>
                              <i className="ti ti-trash" />
                            </button>
                          </div>
                          <div className="resident-form-grid resident-other-insurance-fields">
                            {otherInsuranceFields.map(([field, label]) => (
                              <label className={`resident-form-field${field === "notes" ? " full-span" : ""}`} key={field}>
                                <span>{label}</span>
                                {field === "notes" ? (
                                  <textarea
                                    rows={2}
                                    value={insurance[field] || ""}
                                    onChange={(event) => updateOtherInsurance(insurance.id, field, event.target.value)}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    value={insurance[field] || ""}
                                    onChange={(event) => updateOtherInsurance(insurance.id, field, event.target.value)}
                                  />
                                )}
                              </label>
                            ))}
                          </div>
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="resident-inline-empty">No other insurance added yet.</div>
                  )}
                </div>
              ) : null}
            </section>

            </div>

            <div className="resident-form-notes-column">
              <section className="resident-form-card">
                <h3>Background Notes</h3>
                <CareRichTextEditor
                  height={360}
                  label="Resident background, routines, preferences, and notes"
                  onChange={(value) => updateField("bioNotes", value)}
                  value={formData.bioNotes || ""}
                />
              </section>
              <section className="resident-form-card resident-bio-guidance-card">
                <h3>Form Guidance</h3>
                <ul>
                  <li>Use the left sections for factual resident intake data from assessment files, family, or the resident.</li>
                  <li>Use Care History for admission source, prior care settings, and previous caregiver details.</li>
                  <li>Use Background Notes for routines, preferences, history, risks, and context that helps the care team understand the resident.</li>
                  <li>Keep sensitive identifiers accurate and only reveal masked values when needed.</li>
                </ul>
              </section>
            </div>
          </div>
        </Panel>
      )}

      {activeStep === "contacts" && (
        <Panel title="Contacts and Care Team">
          <div className="resident-contact-toolbar">
            <span>
              <strong>Contacts</strong>
              <small>Add emergency contacts, care coordinators, providers, pharmacy, specialists, and other resident contacts.</small>
            </span>
            <button className="btn primary" type="button" onClick={addContact}>
              <i className="ti ti-plus" /> Add Contact
            </button>
          </div>
          <div className="resident-contact-workspace">
            <aside className="resident-contact-list" aria-label="Added contacts">
              {contacts.length ? contacts.map((contact, index) => {
                const name = formData[contactField(contact.prefix, "name")];
                const organizationName = formData[contactField(contact.prefix, "organizationName")];
                const phone = phoneValueFor(formData, contact.prefix);
                const preferred = preferredPhoneLabel(formData[contactField(contact.prefix, "preferredPhone")]);
                return (
                  <button
                    className={`resident-contact-list-item${contact.id === selectedContact?.id ? " is-active" : ""}`}
                    key={contact.id}
                    onClick={() => setSelectedContactId(contact.id)}
                    type="button"
                  >
                    <span>
                      <strong>{contact.type === "Other" ? contact.relationshipOther || "Other" : contact.type || `Contact ${index + 1}`}</strong>
                      <small>{name || organizationName || contact.notes || "No name or organization added yet"}</small>
                    </span>
                    {phone ? <em>{preferred ? `${preferred}: ${phone}` : phone}</em> : null}
                  </button>
                );
              }) : (
                <div className="resident-contact-empty">
                  <i className="ti ti-address-book" />
                  <strong>No contacts added</strong>
                  <small>Use Add Contact to capture the first relationship or care team contact.</small>
                </div>
              )}
            </aside>
            <div className="resident-contact-editor">
              {selectedContact ? (
                <FacesheetContactCard
                  appConfigLoading={appConfigLoading}
                  canRemove
                  contact={selectedContact}
                  formData={formData}
                  googleMapsApiKey={googleMapsApiKey}
                  key={selectedContact.id}
                  onRemove={removeContact}
                  updateContact={updateContact}
                  updateField={updateField}
                />
              ) : (
                <div className="resident-contact-empty resident-contact-empty-editor">
                  <i className="ti ti-user-plus" />
                  <strong>Select or add a contact</strong>
                  <small>The contact form will appear here after you click Add Contact.</small>
                </div>
              )}
            </div>
          </div>
        </Panel>
      )}

      {activeStep === "careHistory" && (
        <Panel title="Care History">
          <div className="care-history-sections">
            <section className="resident-form-card">
              <h3>Hospital Admission</h3>
              <div className="resident-form-grid compact">
                <label className="resident-form-field full-span">
                  <span>Was the client admitted at a hospital?</span>
                  <select
                    name="wasAdmittedAtHospital"
                    value={formData.wasAdmittedAtHospital || ""}
                    onChange={(event) => updateField("wasAdmittedAtHospital", event.target.value)}
                  >
                    <option value="">Select option</option>
                    {yesNoOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
                {formData.wasAdmittedAtHospital === "Yes" ? (
                  <div className="resident-conditional-fields full-span">
                    <div className="resident-form-section-title">
                      <i className="ti ti-building-hospital" />
                      <span>Admission Details</span>
                    </div>
                    {admissionDetailsFields.map(([name, label, type]) => (
                      <label className="resident-form-field" key={name}>
                        <span>{label}</span>
                        <input name={name} type={type} value={formData[name] || ""} onChange={(event) => updateField(name, event.target.value)} />
                      </label>
                    ))}
                    <ResidentAddressFields
                      appConfigLoading={appConfigLoading}
                      compact
                      formData={formData}
                      googleMapsApiKey={googleMapsApiKey}
                      prefix="hospital"
                      showAddressType={false}
                      title="Hospital Address"
                      updateField={updateField}
                    />
                    <div className="resident-form-field full-span">
                      <CareRichTextEditor
                        height={90}
                        label="Condition, diagnosis, and cause for admission"
                        onChange={(value) => updateField("admissionDescription", value)}
                        placeholder="Capture the resident condition, diagnosis if any, and cause for admission."
                        value={formData.admissionDescription || ""}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="resident-form-card">
              <h3>Previous Family Home</h3>
              <div className="resident-form-grid compact">
                <label className="resident-form-field full-span">
                  <span>Is the client coming from another family home?</span>
                  <select
                    name="fromAnotherFamilyHome"
                    value={formData.fromAnotherFamilyHome || ""}
                    onChange={(event) => updateField("fromAnotherFamilyHome", event.target.value)}
                  >
                    <option value="">Select option</option>
                    {yesNoOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
                {formData.fromAnotherFamilyHome === "Yes" ? (
                  <div className="resident-conditional-fields full-span">
                    <div className="resident-form-section-title">
                      <i className="ti ti-home-heart" />
                      <span>Previous family home details</span>
                    </div>
                    {priorFamilyHomeFields.map(([name, label]) => (
                      <label className="resident-form-field" key={name}>
                        <span>{label}</span>
                        <input
                          name={name}
                          type={name.includes("Email") ? "email" : name.includes("Phone") ? "tel" : name.includes("Years") || name.includes("Months") ? "number" : "text"}
                          min={name.includes("Years") || name.includes("Months") ? "0" : undefined}
                          value={formData[name] || ""}
                          onChange={(event) => updateField(name, event.target.value)}
                        />
                      </label>
                    ))}
                    <ResidentAddressFields
                      appConfigLoading={appConfigLoading}
                      compact
                      formData={formData}
                      googleMapsApiKey={googleMapsApiKey}
                      prefix="priorFamilyHome"
                      showAddressType={false}
                      title="Family Home Address"
                      updateField={updateField}
                    />
                    <label className="resident-form-field full-span">
                      <span>Details</span>
                      <textarea
                        name="priorFamilyHomeDetails"
                        rows={2}
                        value={formData.priorFamilyHomeDetails || ""}
                        onChange={(event) => updateField("priorFamilyHomeDetails", event.target.value)}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="resident-form-card">
              <h3>Prior Caregiver</h3>
              <div className="resident-form-grid compact">
                <label className="resident-form-field full-span">
                  <span>Is there anyone who has been taking care of the client?</span>
                  <select
                    name="hadPriorCaregiver"
                    value={formData.hadPriorCaregiver || ""}
                    onChange={(event) => updateField("hadPriorCaregiver", event.target.value)}
                  >
                    <option value="">Select option</option>
                    {yesNoOptions.map((option) => (
                      <option value={option} key={option}>{option}</option>
                    ))}
                  </select>
                </label>
                {formData.hadPriorCaregiver === "Yes" ? (
                  <div className="resident-conditional-fields full-span">
                    <div className="resident-form-section-title">
                      <i className="ti ti-user-heart" />
                      <span>Prior caregiver details</span>
                    </div>
                    {priorCaregiverFields.map(([name, label]) => (
                      <label className="resident-form-field" key={name}>
                        <span>{label}</span>
                        <input
                          name={name}
                          type={name.includes("Email") ? "email" : name.includes("Phone") ? "tel" : "text"}
                          value={formData[name] || ""}
                          onChange={(event) => updateField(name, event.target.value)}
                        />
                      </label>
                    ))}
                    <ResidentAddressFields
                      appConfigLoading={appConfigLoading}
                      compact
                      formData={formData}
                      googleMapsApiKey={googleMapsApiKey}
                      prefix="priorCaregiver"
                      title="Caregiver Address"
                      updateField={updateField}
                    />
                    <label className="resident-form-field full-span">
                      <span>Care details</span>
                      <textarea
                        name="priorCaregiverDetails"
                        rows={2}
                        value={formData.priorCaregiverDetails || ""}
                        onChange={(event) => updateField("priorCaregiverDetails", event.target.value)}
                      />
                    </label>
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        </Panel>
      )}

      {activeStep === "care" && (
        <Panel title="Initial Care Snapshot">
          <InitialCareSnapshotForm
            values={formData}
            questions={customCareQuestions}
            onChange={updateField}
            onAddQuestion={addCustomCareQuestion}
            onUpdateQuestion={updateCustomCareQuestion}
            onRemoveQuestion={removeCustomCareQuestion}
          />
        </Panel>
      )}

      {activeStep === "preliminaryPlan" && (
        <Panel title="Preliminary Plan">
          <div className="initial-care-snapshot-grid">
            <div className="initial-care-snapshot-main">
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={130}
                  label="Resident goals"
                  onChange={(value) => updateField("preliminaryPlanGoals", value)}
                  value={formData.preliminaryPlanGoals || ""}
                />
              </div>
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={150}
                  label="Care approach"
                  onChange={(value) => updateField("preliminaryPlanCareApproach", value)}
                  value={formData.preliminaryPlanCareApproach || ""}
                />
              </div>
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={150}
                  label="Staff instructions"
                  onChange={(value) => updateField("preliminaryPlanStaffInstructions", value)}
                  value={formData.preliminaryPlanStaffInstructions || ""}
                />
              </div>
            </div>
            <aside className="initial-care-custom-questions">
              <div className="resident-contact-toolbar">
                <span>
                  <strong>Plan Notes</strong>
                  <small>Capture family preferences and immediate follow-up items before documents.</small>
                </span>
              </div>
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={150}
                  label="Family preferences"
                  onChange={(value) => updateField("preliminaryPlanFamilyPreferences", value)}
                  value={formData.preliminaryPlanFamilyPreferences || ""}
                />
              </div>
              <div className="resident-form-field full-span">
                <CareRichTextEditor
                  height={150}
                  label="Risks and follow-up"
                  onChange={(value) => updateField("preliminaryPlanRisksAndFollowUp", value)}
                  value={formData.preliminaryPlanRisksAndFollowUp || ""}
                />
              </div>
            </aside>
          </div>
        </Panel>
      )}

      {activeStep === "documents" && (
        <Panel title="Documents and Signing">
          <div className="resident-documents-grid">
            <section className="resident-form-card resident-documents-upload-card">
              <div className="resident-contact-toolbar">
                <span>
                  <strong>Upload Documents</strong>
                  <small>Attach onboarding agreements, signed policies, photos, and supporting resident files.</small>
                </span>
              </div>
              <label className="assessment-upload assessment-upload-compact resident-document-upload">
                <input
                  className="assessment-file-input"
                  multiple
                  onChange={addDocumentFiles}
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg"
                />
                <i className="ti ti-file-upload" />
                <strong>Attach documents</strong>
                <small>PDF, Word, JPG, or PNG files. Initial assessment pages are view-only on the Source step.</small>
              </label>
              {documentFiles.length ? (
                <div className="resident-document-file-list">
                  {documentFiles.map((document) => {
                    const previewUrl = documentPreviewUrl(document, documentSignedUrls);
                    const downloadUrl = documentDownloadUrl(document, documentSignedUrls);
                    const imagePreview = isImageDocument(document);
                    const canPreview = Boolean(previewUrl) && imagePreview;
                    const canDownload = Boolean(downloadUrl);
                    return (
                    <article className="resident-document-file-row" key={document.id}>
                      <button
                        aria-label={imagePreview ? `Preview ${document.fileName || document.name || "document"}` : `Download ${document.fileName || document.name || "document"}`}
                        className="resident-document-preview-thumb"
                        disabled={imagePreview ? !canPreview : !canDownload}
                        onClick={() => {
                          if (imagePreview) {
                            openDocumentPreview(document);
                          } else if (downloadUrl) {
                            window.open(downloadUrl, "_blank", "noopener,noreferrer");
                          }
                        }}
                        type="button"
                      >
                        {imagePreview && previewUrl ? <img src={previewUrl} alt="" /> : <i className="ti ti-download" />}
                      </button>
                      <div className="resident-document-file-fields">
                        <strong>{document.fileName}</strong>
                        <div className="resident-form-grid compact">
                          <label className="resident-form-field">
                            <span>Name / caption</span>
                            <input value={document.name || ""} onChange={(event) => updateDocumentFile(document.id, "name", event.target.value)} />
                          </label>
                          <label className="resident-form-field">
                            <span>Description</span>
                            <input value={document.description || ""} onChange={(event) => updateDocumentFile(document.id, "description", event.target.value)} />
                          </label>
                        </div>
                        <small>
                          {document.type || "File"} {document.size ? `| ${(document.size / 1024).toFixed(1)} KB` : ""}
                          {document.fileId ? ` | File ID: ${document.fileId}` : " | Pending upload"}
                          {document.confirmed ? " | Confirmed" : ""}
                        </small>
                      </div>
                      {imagePreview ? (
                        <button aria-label={`View ${document.fileName}`} disabled={!canPreview} type="button" onClick={() => openDocumentPreview(document)}>
                          <i className="ti ti-eye" />
                        </button>
                      ) : (
                        <a
                          aria-label={`Download ${document.fileName}`}
                          className={`resident-icon-link ${canDownload ? "" : "disabled"}`}
                          href={canDownload ? downloadUrl : undefined}
                          target="_blank"
                          rel="noreferrer"
                          download={document.fileName || document.name || true}
                        >
                          <i className="ti ti-download" />
                        </a>
                      )}
                      <button aria-label={`Remove ${document.fileName}`} type="button" onClick={() => removeDocumentFile(document.id)}>
                        <i className="ti ti-x" />
                      </button>
                    </article>
                    );
                  })}
                </div>
              ) : (
                <div className="resident-inline-empty">No uploaded documents added yet.</div>
              )}
              {sourceAssessmentPageList.length ? (
                <div className="resident-assessment-pages-card">
                {renderReadOnlyAssessmentPages("Read-only pages from the initial assessment. They are not saved again with onboarding documents.")}
                </div>
              ) : null}
            </section>

            <section className="resident-form-card resident-signed-validation-card">
              <div className="resident-contact-toolbar">
                <span>
                  <strong>Policy / Document Signing Validation</strong>
                  <small>Confirm signed verification and whether the signed file has been uploaded for each policy or document.</small>
                </span>
                <button className="btn primary" type="button" onClick={addSignedDocumentValidation}>
                  <i className="ti ti-plus" /> Add Document
                </button>
              </div>
              <div className="resident-signed-validation-list">
                {signedDocumentValidations.map((document) => (
                  <article className="resident-signed-validation-row" key={document.id}>
                    <label className="resident-form-field resident-signed-document-name">
                      <span>Document / policy</span>
                      <input value={document.documentName || ""} onChange={(event) => updateSignedDocumentValidation(document.id, "documentName", event.target.value)} />
                    </label>
                    <label className="resident-check-card">
                      <input
                        checked={Boolean(document.signedVerified)}
                        onChange={(event) => updateSignedDocumentValidation(document.id, "signedVerified", event.target.checked)}
                        type="checkbox"
                      />
                      <span><strong>Signed verification</strong><small>Signature confirmed</small></span>
                    </label>
                    <label className="resident-check-card">
                      <input
                        checked={Boolean(document.fileUploaded)}
                        onChange={(event) => updateSignedDocumentValidation(document.id, "fileUploaded", event.target.checked)}
                        type="checkbox"
                      />
                      <span><strong>File uploaded</strong><small>Signed file attached</small></span>
                    </label>
                    <label className="resident-form-field resident-signed-notes">
                      <span>Notes</span>
                      <input value={document.notes || ""} onChange={(event) => updateSignedDocumentValidation(document.id, "notes", event.target.value)} />
                    </label>
                    <button aria-label={`Remove ${document.documentName || "document validation"}`} type="button" onClick={() => removeSignedDocumentValidation(document.id)}>
                      <i className="ti ti-trash" />
                    </button>
                  </article>
                ))}
              </div>
            </section>
          </div>
        </Panel>
      )}

      {activeStep === "review" && (
        <Panel title="Review and Submit">
          <div className="resident-onboarding-status resident-review-preview-status" role="status">
            <i className={`ti ${isResidentActive ? "ti-circle-check" : onboardingPreview ? "ti-eye-check" : "ti-eye"}`} />
            <span>
              <strong>
                {isResidentActive
                  ? "Resident onboarded successfully"
                  : onboardingPreview
                    ? "Preview loaded from saved onboarding details"
                    : "Previewing current onboarding details"}
              </strong>
              <small>
                {isResidentActive
                  ? "No more action is required in the onboarding requirements."
                  : onboardingPreview?.registrationId
                  ? `Registration draft ${onboardingPreview.registrationId}`
                  : onboardingResidentId
                    ? "Saved resident details will be confirmed from this step."
                    : "Save Bio Data first to create the resident before confirming."}
              </small>
            </span>
          </div>
          <div className="resident-preview-grid">
            {previewSections.map((section) => (
              <article className={`resident-preview-card tone-${section.tone || "default"}`} key={section.title}>
                <div className="resident-preview-card__head">
                  <span><i className={`ti ${section.icon || "ti-circle-check"}`} /></span>
                  <div>
                    <strong>{section.title}</strong>
                    <small>{section.rows.filter(([, value]) => Boolean(value)).length} completed fields</small>
                  </div>
                </div>
                <dl>
                  {section.rows.map(([label, value]) => (
                    <div className={previewRowIsWide(label) ? "is-wide" : ""} key={label}>
                      <dt>{label}</dt>
                      <dd className={!value ? "is-empty" : ""}>
                        {value && previewValueIsRich(value) ? (
                          <span className="resident-preview-rich" dangerouslySetInnerHTML={{ __html: value }} />
                        ) : (
                          previewValue(value)
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>
          <div className="resident-submit-panel">
            <span>
              <strong>{isResidentActive ? "Resident onboarded successfully" : "Ready to create resident?"}</strong>
              <small>
                {isResidentActive
                  ? "No more action is required. Open the Resident Details page to continue managing care."
                  : "The resident profile will be added to this workspace and can move into assessment, MaG, preliminary care plan, and negotiated care plan flows."}
              </small>
            </span>
            {isResidentActive && activeResidentId ? (
              <Link className="btn primary" to={`/residents/${activeResidentId}`}>
                <i className="ti ti-user-circle" /> Resident Details
              </Link>
            ) : !isResidentActive ? (
              <button className="btn primary" type="button" onClick={confirmSubmitResident} disabled={submitting}>
                <i className="ti ti-user-check" /> {submitting ? "Confirming..." : "Confirm Details"}
              </button>
            ) : null}
          </div>
        </Panel>
      )}

      <div className="resident-step-actions">
        <button className="mini-btn" type="button" onClick={goBack} disabled={isFirstStep}><i className="ti ti-arrow-left" /> Back</button>
        <div className="resident-step-action-group">
          {!isLastStep && activeStep !== "source" ? (
            <button className="mini-btn resident-save-step-btn" type="button" onClick={saveCurrentStep} disabled={submitting || filesUploading}>
              <i className="ti ti-device-floppy" /> {savingAction === "save" ? (filesUploading ? "Uploading..." : "Saving...") : "Save"}
            </button>
          ) : null}
          {!isLastStep ? (
            <button
              className="btn primary"
              type="button"
              onClick={continueToNextStep}
              disabled={submitting || filesUploading}
            >
              {savingAction === "continue" ? "Saving..." : "Continue"} <i className="ti ti-arrow-right" />
            </button>
          ) : !isResidentActive ? (
            <button className="btn primary" type="button" onClick={confirmSubmitResident} disabled={submitting}>
              <i className="ti ti-user-check" /> {submitting ? "Confirming..." : "Confirm Details"}
            </button>
          ) : (
            <span className="resident-confirmed-status">
              <i className="ti ti-circle-check" /> Confirmed
            </span>
          )}
        </div>
      </div>
      {filePreview ? (
        <div className="resident-file-preview-modal" role="dialog" aria-modal="true" aria-label={filePreview.name}>
          <div className="resident-file-preview-backdrop" onClick={closeFilePreview} />
          <div className="resident-file-preview-dialog">
            <div className="resident-file-preview-head">
              <span>
                <strong>{filePreview.title || filePreview.name}</strong>
                <small>
                  {filePreview.entries?.length > 1 ? `Page ${Number(filePreview.currentIndex || 0) + 1} of ${filePreview.entries.length} - ` : ""}
                  {filePreview.name}
                  {filePreview.type ? ` - ${filePreview.type}` : ""}
                </small>
              </span>
              <div className="resident-file-preview-actions">
                {filePreview.entries?.length > 1 ? (
                  <>
                    <button className="mini-btn icon-only" aria-label="Previous page" type="button" onClick={() => showFilePreviewEntry(Number(filePreview.currentIndex || 0) - 1)}>
                      <i className="ti ti-chevron-left" />
                    </button>
                    <button className="mini-btn icon-only" aria-label="Next page" type="button" onClick={() => showFilePreviewEntry(Number(filePreview.currentIndex || 0) + 1)}>
                      <i className="ti ti-chevron-right" />
                    </button>
                  </>
                ) : null}
                <button className="mini-btn icon-only" aria-label="Rotate left" type="button" onClick={() => setFilePreviewRotation((rotation) => rotation - 90)}>
                  <i className="ti ti-rotate-2" />
                </button>
                <button className="mini-btn icon-only" aria-label="Rotate right" type="button" onClick={() => setFilePreviewRotation((rotation) => rotation + 90)}>
                  <i className="ti ti-rotate-clockwise-2" />
                </button>
                <a className="mini-btn icon-only" aria-label="Open in new tab" href={filePreview.url} target="_blank" rel="noreferrer"><i className="ti ti-external-link" /></a>
                <button className="mini-btn icon-only" aria-label="Close preview" type="button" onClick={closeFilePreview}><i className="ti ti-x" /></button>
              </div>
            </div>
            <div className="resident-file-preview-body">
              {filePreview.isImage ? (
                <img src={filePreview.url} alt={filePreview.name} style={{ transform: `rotate(${filePreviewRotation}deg)` }} />
              ) : (
                <iframe src={filePreview.url} title={filePreview.name} style={{ transform: `rotate(${filePreviewRotation}deg)` }} />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </CareShell>
  );
}
